import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Briefcase,
  Calendar,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  Clock3,
  Download,
  Pencil,
  Edit,
  FileText,
  HeartPulse,
  Info,
  Lock,
  Mail,
  MessageSquareText,
  Phone,
  Plus,
  Scale,
  Trash2,
  Upload,
  User,
  UserRoundPlus,
  X,
} from 'lucide-react';
import { LoadingSpinner, InlineLoadingState } from '../components/LoadingSpinner';
import { AppSidebar } from '../components/AppSidebar';
import { AppTopBar } from '../components/AppTopBar';
import { AppSelect } from '../components/AppSelect';
import { AppSelectDropdown, type AppSelectOption } from '../components/AppSelectDropdown';
import { DatePicker } from '../components/DatePicker';
import { EmployeeListRail } from '../components/employees/EmployeeListRail';
import { EmployeeOverviewShell, overviewTabFromParam } from '../components/employees/EmployeeOverviewShell';
import { EmployeeOverviewDashboardTab } from '../components/employees/overview/EmployeeOverviewDashboardTab';
import { EmployeeOverviewRequestsTab } from '../components/employees/overview/EmployeeOverviewRequestsTab';
import { EmployeeBeAbzuegeTab } from '../components/employees/be-abzuege/EmployeeBeAbzuegeTab';
import { EmployeeProfileBeAbzuegeTab } from '../components/employees/be-abzuege/EmployeeProfileBeAbzuegeTab';
import { useEmployeePeriodQuery } from '../components/employees/overview/useEmployeePeriodQuery';
import { EmployeePayrollTab } from '../components/employees/payroll/EmployeePayrollTab';
import { EmployeeMasterDataHistory } from '../components/employees/EmployeeMasterDataHistory';
import { PayrollPeriodClosedBadge, PayrollPeriodClosedBanner } from '../components/employees/PayrollPeriodClosedHint';
import { InboxAttachmentPreviewModal } from '../features/inbox/components/InboxAttachmentPreviewModal';
import { UserInviteModal } from '../features/settings/components/UserInviteModal';
import { AppConfirmDialog } from '../components/AppConfirmDialog';
import type { RolePresetOption } from '../features/settings/components/RolePresetSelect';
import type { CapabilityMap } from '../features/settings/capabilitiesUi';
import { useCapabilities } from '../hooks/useCapabilities';
import type { InboxAttachmentPreviewTarget } from '../features/inbox/inboxAttachmentUtils';
import {
  employeeDetailPath,
  employeesOverviewPath,
  isEmployeeDetailTab,
  monthQueryParamFromDate,
  parseMonthQueryParam,
  type EmployeeDetailTab,
} from '../utils/employeeRoutes';
import {
  EMPLOYEE_DOCUMENT_TYPE_OTHER_KEY,
  employeeDocumentFilterOptions,
  employeeDocumentTypeLabel,
  enabledEmployeeDocumentTypes,
  fallbackEmployeeDocumentTypes,
  parseEmployeeDocumentTypeRows,
  type EmployeeDocumentTypeRow,
} from '../utils/employeeDocumentTypes';
import {
  buildPendingChangeRequestMap,
  effectiveEntryTypeForPendingDay,
  resolvePendingListEntryFields,
  hasPendingChangeRequestForDate,
  normalizeChangeRequestDate,
  pendingInfoFromChangeRequest,
  type PendingChangeRequestInfo,
} from '../utils/pendingChangeRequests';
import {
  calcWorkHours,
  formatDisplayHours,
  isFixedDayHoursEntryType,
  isHolidayCreditDate,
  isHourTrackingEntryType,
  isWeekdayYmd,
  normalizeBreakMinutes,
} from '../utils/employeeTimeEntryHours';
import {
  buildPayrollPeriodStatusMap,
  isClosedPayrollMonth,
  payrollYearMonthKey,
  type PayrollPeriodListItem,
  type PayrollPeriodStatus,
} from '../utils/payrollPeriodStatus';
import {
  ENTRY_TYPE_COLORS,
  ENTRY_TYPE_LABEL,
  ENTRY_TYPE_ORDER,
  type EmployeeTimeEntryType,
} from '../components/employees/employeeTimeEntryConstants';
import {
  COMPENSATION_TYPE_OPTIONS,
  EMPLOYEE_STAMMDATEN_EXPORT_GROUPS,
  EMPLOYMENT_TYPE_OPTIONS,
  EMPLOYMENT_TYPE_VALUE_SET,
  MARITAL_STATUS_OPTIONS,
  MARITAL_STATUS_VALUE_SET,
  RELIGION_OPTIONS,
  RELIGION_VALUE_SET,
  TAX_CLASS_OPTIONS,
  TAX_CLASS_VALUE_SET,
  WORK_AREA_OPTIONS,
  WORK_AREA_VALUE_SET,
  toStammdatenSectionFields,
} from '../utils/employeeStammdatenExport';
import type { EmployeeCompensationType } from '../utils/payrollSummaryCalc';
import {
  type EmployeeWorkAreaFilter,
  matchesEmployeeWorkAreaFilter,
  readEmployeeWorkAreaFilter,
  readInitialEmployeeWorkAreaFilter,
  sanitizeWorkAreaFilter,
  writeEmployeeWorkAreaFilter,
} from '../utils/employeeWorkAreaFilter';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import {
  comparePlantafelEmployees,
  type PlantafelMembershipRole,
} from '../utils/plantafelEmployeeOrder';

type Employee = {
  id: string;
  user_id: string | null;
  is_archived: boolean;
  color_idx: number;
  first_name: string;
  last_name: string;
  birth_date: string | null;
  birth_name: string;
  birth_place: string;
  birth_country: string;
  street: string;
  house_number: string;
  postal_code: string;
  city: string;
  driving_license_classes: string[];
  phone: string;
  email: string;
  department: string;
  position: string;
  work_area: string;
  start_date: string | null;
  end_date: string | null;
  iban: string;
  bank_name: string;
  bank_account_holder: string;
  social_security_number: string;
  personal_number: string;
  tax_id: string;
  employment_type: string;
  weekly_hours: number;
  compensation_type: EmployeeCompensationType;
  hourly_wage: number;
  monthly_salary: number;
  allowance_per_hour: number;
  vma_tax_free_per_day: number;
  overnight_tax_free_per_day: number;
  health_insurance_name: string;
  tax_class: string;
  religion_code: string;
  child_allowance_count: number;
  marital_status: string;
  nationality_code: string;
  severe_disability: boolean;
  emergency_contact_name: string;
  emergency_contact_relation: string;
  emergency_contact_phone: string;
  expected_hours_per_day: number;
  statutory_vacation_days: number;
  additional_vacation_days: number;
  created_at: string;
  updated_at: string;
  last_seen_at: string | null;
  membership_role?: PlantafelMembershipRole | null;
};

function employeeSortName(emp: Pick<Employee, 'first_name' | 'last_name' | 'email'>): string {
  const full = `${emp.first_name} ${emp.last_name}`.trim();
  if (full) return full;
  const mail = emp.email.trim();
  if (mail) return mail;
  return 'Mitarbeiter';
}

type EmployeeDocument = {
  id: string;
  employee_id: string;
  category: string;
  display_name: string;
  mime_type?: string | null;
  size_bytes: number;
  created_at: string;
};

type TimeEntry = {
  id: string;
  employee_id: string;
  entry_date: string;
  entry_type: EmployeeTimeEntryType;
  start_time: string | null;
  end_time: string | null;
  /** Net work breaks (minutes); only applies to entry types that track hours. */
  break_minutes?: number;
  note: string;
  activity_id?: string | null;
  /** Project time without appointment. */
  lead_id?: string | null;
  /** Titles flattened by GET /time-entries for appointment/project intervals. */
  activity_title?: string | null;
  lead_title?: string | null;
  created_at: string;
  updated_at: string;
};

/** Appointment or project interval — not edited by the day form. */
function isLinkedWorkEntry(entry: TimeEntry): boolean {
  return entry.entry_type === 'work' && Boolean(entry.activity_id || entry.lead_id);
}

/** Replaces the day-keyed row (absence or day stamp) of `date`; appointment/project intervals stay. */
function withDayKeyedEntry(entries: TimeEntry[], date: string, item: TimeEntry | null): TimeEntry[] {
  const next = entries.filter((entry) => entry.entry_date !== date || isLinkedWorkEntry(entry));
  if (item) next.push(item);
  return next.sort((a, b) => a.entry_date.localeCompare(b.entry_date));
}

function pickDayKeyedTimeEntry(entries: TimeEntry[]): TimeEntry | undefined {
  const absence = entries.find((e) => e.entry_type !== 'work');
  if (absence) return absence;
  return entries.find((e) => e.entry_type === 'work' && !e.activity_id && !e.lead_id);
}

/** Calendar cell: day stamp if present, otherwise any work row so Ist hours still show. */
function pickCalendarDayEntry(entries: TimeEntry[]): TimeEntry | undefined {
  return pickDayKeyedTimeEntry(entries) ?? entries.find((e) => e.entry_type === 'work');
}

function sumWorkHoursOnDate(entries: TimeEntry[], date: string): number {
  return entries
    .filter((e) => e.entry_date === date && e.entry_type === 'work')
    .reduce((acc, e) => acc + calcWorkHours(e.start_time, e.end_time, e.break_minutes), 0);
}

type Holiday = {
  holiday_date: string;
  name: string;
  country_code: string;
  subdivision_code?: string | null;
};

type TabId = EmployeeDetailTab;
type DocumentFilter = 'all' | string;

const AVATAR_COLORS = [
  { bg: 'bg-blue-100', text: 'text-blue-700' },
  { bg: 'bg-indigo-100', text: 'text-indigo-700' },
  { bg: 'bg-violet-100', text: 'text-violet-700' },
  { bg: 'bg-fuchsia-100', text: 'text-fuchsia-700' },
  { bg: 'bg-amber-100', text: 'text-amber-700' },
  { bg: 'bg-emerald-100', text: 'text-emerald-700' },
];

/** Virtual calendar rows from `holiday_cache` (id prefix `holiday-`), not stored in DB. */
function isAutoHolidayEntry(entry: TimeEntry): boolean {
  return entry.id.startsWith('holiday-');
}

function canDeletePersistedTimeEntry(date: string | null, entries: TimeEntry[]): boolean {
  if (!date) return false;
  const persisted = pickDayKeyedTimeEntry(entries.filter((entry) => entry.entry_date === date));
  return Boolean(persisted && !isAutoHolidayEntry(persisted));
}

const YES_NO_OPTIONS = [
  { value: 'false', label: 'Nein' },
  { value: 'true', label: 'Ja' },
];

const STAMMDATEN_SECTION_ICONS: Record<string, React.ReactNode> = {
  personal: <User className="w-4 h-4 text-app-text-subtle" />,
  job: <Briefcase className="w-4 h-4 text-app-text-subtle" />,
  finance: <FileText className="w-4 h-4 text-app-text-subtle" />,
  reporting: <Mail className="w-4 h-4 text-app-text-subtle" />,
  compensation: <FileText className="w-4 h-4 text-app-text-subtle" />,
  emergency: <HeartPulse className="w-4 h-4 text-app-text-subtle" />,
};

function getInitials(emp: Pick<Employee, 'first_name' | 'last_name'>) {
  return `${(emp.first_name || '?').charAt(0)}${(emp.last_name || '').charAt(0)}`.toUpperCase();
}

function formatDate(date: string | null) {
  if (!date) return '-';
  const d = new Date(`${date}T00:00:00`);
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleDateString('de-DE');
}

function formatDateTime(date: string | null) {
  if (!date) return 'Noch nie';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return 'Noch nie';
  return d.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
}

function formatLastSeenLabel(date: string | null): { text: string; isToday: boolean } {
  if (!date) return { text: 'Noch nie', isToday: false };
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return { text: 'Noch nie', isToday: false };

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const target = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const time = d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

  if (target.getTime() === today.getTime()) {
    return { text: `Heute, ${time} Uhr`, isToday: true };
  }
  if (target.getTime() === yesterday.getTime()) {
    return { text: `Gestern, ${time} Uhr`, isToday: false };
  }

  const datePart = d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
  return { text: `${datePart}, ${time}`, isToday: false };
}

function formatTimestamp(date: string | null) {
  if (!date) return '-';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('de-DE');
}

function formatBytes(bytes: number) {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let i = 0;
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024;
    i += 1;
  }
  return `${size.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function formatIban(value: string | null | undefined) {
  const cleaned = String(value ?? '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
  if (!cleaned) return '';
  return cleaned.match(/.{1,4}/g)?.join(' ') ?? cleaned;
}

function normalizeIban(value: string | null | undefined) {
  return String(value ?? '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
}

const IBAN_LENGTH_BY_COUNTRY: Record<string, number> = {
  DE: 22,
  PL: 28,
};

function isValidIban(value: string | null | undefined) {
  const normalized = normalizeIban(value);
  if (!normalized) return true;
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(normalized)) return false;

  const country = normalized.slice(0, 2);
  const expectedLength = IBAN_LENGTH_BY_COUNTRY[country];
  if (expectedLength && normalized.length !== expectedLength) return false;
  if (!expectedLength && (normalized.length < 15 || normalized.length > 34)) return false;

  const rearranged = `${normalized.slice(4)}${normalized.slice(0, 4)}`;
  let remainder = 0;
  for (const ch of rearranged) {
    const asDigits = ch >= 'A' && ch <= 'Z' ? String(ch.charCodeAt(0) - 55) : ch;
    for (const digit of asDigits) {
      remainder = (remainder * 10 + Number(digit)) % 97;
    }
  }
  return remainder === 1;
}

function normalizeTaxId(value: string | null | undefined) {
  return String(value ?? "").replace(/\D/g, "").slice(0, 11);
}

function isValidTaxId(value: string | null | undefined) {
  const normalized = normalizeTaxId(value);
  return normalized.length === 0 || normalized.length === 11;
}

function parseLocaleNumber(value: unknown, fallback = 0): number {
  const raw = String(value ?? '').trim();
  if (!raw) return fallback;
  const normalized = raw.replace(',', '.');
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function formatLocaleNumber(value: unknown) {
  const parsed = parseLocaleNumber(value, Number.NaN);
  if (!Number.isFinite(parsed)) return '-';
  return parsed.toLocaleString('de-DE', { maximumFractionDigits: 2 });
}

function toHm(time: string | null) {
  if (!time) return '';
  return time.slice(0, 5);
}

function monthBounds(anchor: Date) {
  const year = anchor.getFullYear();
  const month = anchor.getMonth();
  const from = new Date(year, month, 1);
  const to = new Date(year, month + 1, 0);
  return { from, to };
}

function toDateString(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function weekdayMon0(d: Date) {
  return (d.getDay() + 6) % 7;
}

function ymdToLocalDate(ymd: string) {
  const [y, m, d] = ymd.split('-').map((x) => Number.parseInt(x, 10));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  return new Date(y, m - 1, d);
}

function yearBounds(year: number) {
  return { from: `${year}-01-01`, to: `${year}-12-31` };
}

function formatVacationDayCount(value: number) {
  const rounded = Math.round(value * 10) / 10;
  if (Number.isInteger(rounded)) return String(rounded);
  return rounded.toFixed(1).replace('.', ',');
}

/** Einstiegsmonat: anteilig nach Tagen, wenn nicht am 1. gestartet; sonst voller Monatsanteil (1/12). */
function proratedAnnualVacationEntitlement(
  statutoryDays: number,
  additionalDays: number,
  startDate: string | null,
  endDate: string | null,
  year: number
) {
  const statutoryFull = Math.max(0, statutoryDays);
  const additionalFull = Math.max(0, additionalDays);
  const fullYear = statutoryFull + additionalFull;
  if (fullYear <= 0) {
    return {
      entitlement: 0,
      fullYear,
      statutoryFull,
      additionalFull,
      isProrated: false,
      hasPartialStartMonth: false,
    };
  }

  const monthlyShare = fullYear / 12;
  const startYmd = startDate?.slice(0, 10) ?? null;
  const endYmd = endDate?.slice(0, 10) ?? null;
  let entitlement = 0;
  let hasPartialStartMonth = false;

  for (let month = 0; month < 12; month += 1) {
    const monthStart = new Date(year, month, 1);
    const monthEnd = new Date(year, month + 1, 0);
    const daysInMonth = monthEnd.getDate();
    const monthStartYmd = toDateString(monthStart);
    const monthEndYmd = toDateString(monthEnd);

    let employFromYmd = monthStartYmd;
    let employToYmd = monthEndYmd;
    if (startYmd && startYmd > employFromYmd) employFromYmd = startYmd;
    if (endYmd && endYmd < employToYmd) employToYmd = endYmd;
    if (employFromYmd > employToYmd) continue;

    const employFrom = ymdToLocalDate(employFromYmd);
    const employTo = ymdToLocalDate(employToYmd);
    if (!employFrom || !employTo) continue;

    const msPerDay = 24 * 60 * 60 * 1000;
    const daysEmployedInMonth =
      Math.floor((employTo.getTime() - employFrom.getTime()) / msPerDay) + 1;

    const isStartMonth =
      startYmd != null && startYmd >= monthStartYmd && startYmd <= monthEndYmd;
    const startsAfterFirst = isStartMonth && Number.parseInt(startYmd!.slice(8, 10), 10) > 1;

    let monthFactor = 1;
    if (startsAfterFirst) {
      hasPartialStartMonth = true;
      monthFactor = daysEmployedInMonth / daysInMonth;
    }

    entitlement += monthlyShare * monthFactor;
  }

  const roundedEntitlement = Math.round(entitlement * 2) / 2;
  const isProrated = roundedEntitlement < fullYear - 0.001;

  return {
    entitlement: roundedEntitlement,
    fullYear,
    statutoryFull,
    additionalFull,
    isProrated,
    hasPartialStartMonth,
  };
}

function isWithinEmploymentYmd(ymd: string, startDate: string | null, endDate: string | null) {
  const d = ymd.slice(0, 10);
  if (startDate && d < startDate.slice(0, 10)) return false;
  if (endDate && d > endDate.slice(0, 10)) return false;
  return true;
}

function countVacationWeekdays(
  entries: TimeEntry[],
  fromYmd: string,
  toYmd: string,
  todayYmd: string,
  startDate: string | null,
  endDate: string | null,
  mode: 'all' | 'taken' | 'planned'
) {
  return entries.filter((e) => {
    if (e.entry_type !== 'vacation') return false;
    const d = e.entry_date.slice(0, 10);
    if (d < fromYmd || d > toYmd) return false;
    if (!isWeekdayYmd(d)) return false;
    if (!isWithinEmploymentYmd(d, startDate, endDate)) return false;
    if (mode === 'taken' && d > todayYmd) return false;
    if (mode === 'planned' && d <= todayYmd) return false;
    return true;
  }).length;
}

function buildMonthGrid(anchor: Date) {
  const { from } = monthBounds(anchor);
  const start = new Date(from);
  start.setDate(start.getDate() - weekdayMon0(start));
  const cells: Date[] = [];
  for (let i = 0; i < 42; i += 1) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    cells.push(d);
  }
  return cells;
}

/** ISO 8601 week number: weeks start Monday, week 1 contains the year's first Thursday. */
function getIsoWeek(date: Date): number {
  const utc = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = utc.getUTCDay() || 7; // Mon=1..Sun=7
  utc.setUTCDate(utc.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(utc.getUTCFullYear(), 0, 1));
  return Math.ceil(((utc.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
}

type WizardState = {
  first_name: string;
  last_name: string;
  birth_date: string;
  birth_name: string;
  birth_place: string;
  birth_country: string;
  street: string;
  house_number: string;
  postal_code: string;
  city: string;
  email: string;
  phone: string;
  department: string;
  position: string;
  work_area: string;
  start_date: string;
  end_date: string;
  driving_license_classes: string;
  iban: string;
  bank_name: string;
  bank_account_holder: string;
  social_security_number: string;
  personal_number: string;
  tax_id: string;
  employment_type: string;
  weekly_hours: string;
  compensation_type: EmployeeCompensationType;
  hourly_wage: string;
  monthly_salary: string;
  allowance_per_hour: string;
  vma_tax_free_per_day: string;
  overnight_tax_free_per_day: string;
  health_insurance_name: string;
  tax_class: string;
  religion_code: string;
  child_allowance_count: string;
  marital_status: string;
  nationality_code: string;
  severe_disability: boolean;
  emergency_contact_name: string;
  emergency_contact_relation: string;
  emergency_contact_phone: string;
  color_idx: number;
  statutory_vacation_days: string;
  additional_vacation_days: string;
};

function workAreaSelectOptions(canAccessInternal: boolean) {
  if (canAccessInternal) return WORK_AREA_OPTIONS;
  return WORK_AREA_OPTIONS.filter((option) => option.value === 'field');
}

function NewEmployeeWizard({
  onClose,
  onSave,
  showInviteHint = false,
  canAccessInternal = true,
}: {
  onClose: () => void;
  onSave: (payload: Partial<Employee>) => Promise<void>;
  showInviteHint?: boolean;
  canAccessInternal?: boolean;
}) {
  const [step, setStep] = React.useState(0);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [taxIdSaveAttempted, setTaxIdSaveAttempted] = React.useState(false);
  const [form, setForm] = React.useState<WizardState>({
    first_name: '',
    last_name: '',
    birth_date: '',
    birth_name: '',
    birth_place: '',
    birth_country: '',
    street: '',
    house_number: '',
    postal_code: '',
    city: '',
    email: '',
    phone: '',
    department: '',
    position: '',
    work_area: canAccessInternal ? '' : 'field',
    start_date: '',
    end_date: '',
    driving_license_classes: '',
    iban: '',
    bank_name: '',
    bank_account_holder: '',
    social_security_number: '',
    personal_number: '',
    tax_id: '',
    employment_type: '',
    weekly_hours: '',
    compensation_type: 'hourly',
    hourly_wage: '',
    monthly_salary: '',
    allowance_per_hour: '',
    vma_tax_free_per_day: '',
    overnight_tax_free_per_day: '',
    health_insurance_name: '',
    tax_class: '',
    religion_code: '',
    child_allowance_count: '',
    marital_status: '',
    nationality_code: '',
    severe_disability: false,
    emergency_contact_name: '',
    emergency_contact_relation: '',
    emergency_contact_phone: '',
    color_idx: Math.floor(Math.random() * AVATAR_COLORS.length),
    statutory_vacation_days: '',
    additional_vacation_days: '',
  });

  const canContinue = step === 0 ? Boolean(form.first_name && form.last_name) : true;
  const ibanInvalid = !isValidIban(form.iban);
  const taxIdInvalid = form.tax_id.length > 0 && form.tax_id.length !== 11;
  const showIbanError = taxIdSaveAttempted && ibanInvalid;
  const showTaxIdError = taxIdSaveAttempted && taxIdInvalid;
  const selectedColor = AVATAR_COLORS[form.color_idx % AVATAR_COLORS.length];
  const displayName = [form.first_name, form.last_name].filter(Boolean).join(' ');
  const steps = ['Persönliche Daten', 'Sonstige Daten', 'Übersicht'];
  const summaryRows = [
    ['Geburtsdatum', form.birth_date ? formatDate(form.birth_date) : ''],
    ['Geburtsname', form.birth_name],
    ['Geburtsort', form.birth_place],
    ['Geburtsland', form.birth_country],
    ['Straße', form.street],
    ['Hausnummer', form.house_number],
    ['PLZ', form.postal_code],
    ['Ort', form.city],
    ['Telefon', form.phone],
    ['E-Mail', form.email],
    ['Personalnummer', form.personal_number],
    ['Abteilung', form.department],
    ['Position', form.position],
    ['Arbeitsbereich', WORK_AREA_OPTIONS.find((x) => x.value === form.work_area)?.label || form.work_area],
    ['Beschäftigungsart', EMPLOYMENT_TYPE_OPTIONS.find((x) => x.value === form.employment_type)?.label || form.employment_type],
    ['Wöchentliche Arbeitsstunden', form.weekly_hours],
    ['Gesetzlicher Urlaub', form.statutory_vacation_days],
    ['Zusätzlicher Urlaub', form.additional_vacation_days],
    ['Eintritt', form.start_date ? formatDate(form.start_date) : ''],
    ['Austritt', form.end_date ? formatDate(form.end_date) : ''],
    ['Führerscheinklassen', form.driving_license_classes],
    ['Kontoinhaber', form.bank_account_holder],
    ['IBAN', form.iban],
    ['Bank', form.bank_name],
    ['Sozialversicherungsnr.', form.social_security_number],
    ['Steuer-ID', form.tax_id],
    ['Vergütungsart', COMPENSATION_TYPE_OPTIONS.find((x) => x.value === form.compensation_type)?.label || form.compensation_type],
    ...(form.compensation_type === 'salary'
      ? [['Monatliches Fixum', form.monthly_salary] as [string, string]]
      : [
          ['Stundenlohn', form.hourly_wage] as [string, string],
          ['Zulage/Stunde', form.allowance_per_hour] as [string, string],
        ]),
    ['Steuerfreie VMA/Tag', form.vma_tax_free_per_day],
    ['Steuerfreie Übernachtung/Tag', form.overnight_tax_free_per_day],
    ['Krankenkasse', form.health_insurance_name],
    ['Steuerklasse', TAX_CLASS_OPTIONS.find((x) => x.value === form.tax_class)?.label || form.tax_class],
    ['Konfession', RELIGION_OPTIONS.find((x) => x.value === form.religion_code)?.label || form.religion_code],
    ['Kinderfreibetrag', form.child_allowance_count],
    ['Familienstand', MARITAL_STATUS_OPTIONS.find((x) => x.value === form.marital_status)?.label || form.marital_status],
    ['Staatsangehörigkeit', form.nationality_code],
    ['Schwerbehinderung', form.severe_disability ? 'Ja' : 'Nein'],
    ['Notfallkontakt Name', form.emergency_contact_name],
    ['Notfallkontakt Beziehung', form.emergency_contact_relation],
    ['Notfallkontakt Telefon', form.emergency_contact_phone],
  ].filter(([, value]) => String(value).trim().length > 0);

  const submit = async () => {
    setTaxIdSaveAttempted(true);
    const normalizedIban = normalizeIban(form.iban);
    if (!isValidIban(normalizedIban)) {
      setError("Ungültige IBAN.");
      return;
    }
    const normalizedTaxId = normalizeTaxId(form.tax_id);
    if (!isValidTaxId(normalizedTaxId)) {
      setError("Steuer-ID muss genau 11 Ziffern haben.");
      return;
    }
    if (!EMPLOYMENT_TYPE_VALUE_SET.has(form.employment_type)) {
      setError("Ungültige Beschäftigungsart.");
      return;
    }
    if (!WORK_AREA_VALUE_SET.has(form.work_area)) {
      setError("Ungültiger Arbeitsbereich.");
      return;
    }
    const normalizedTaxClass = String(form.tax_class ?? '').trim();
    if (!TAX_CLASS_VALUE_SET.has(normalizedTaxClass)) {
      setError("Ungültige Steuerklasse.");
      return;
    }
    if (!RELIGION_VALUE_SET.has(form.religion_code)) {
      setError("Ungültige Konfession.");
      return;
    }
    if (!MARITAL_STATUS_VALUE_SET.has(form.marital_status)) {
      setError("Ungültiger Familienstand.");
      return;
    }
    if (form.nationality_code && !/^[A-Z]{2}$/.test(form.nationality_code.trim().toUpperCase())) {
      setError("Staatsangehörigkeit muss ein ISO-Ländercode (2 Buchstaben) sein.");
      return;
    }
    if (parseLocaleNumber(form.child_allowance_count, 0) < 0) {
      setError("Kinderfreibetrag darf nicht negativ sein.");
      return;
    }
    if (parseLocaleNumber(form.weekly_hours, 0) < 0) {
      setError("Wöchentliche Arbeitsstunden dürfen nicht negativ sein.");
      return;
    }
    if (parseLocaleNumber(form.hourly_wage, 0) < 0) {
      setError("Stundenlohn darf nicht negativ sein.");
      return;
    }
    if (parseLocaleNumber(form.monthly_salary, 0) < 0) {
      setError("Monatliches Fixum darf nicht negativ sein.");
      return;
    }
    if (parseLocaleNumber(form.statutory_vacation_days, 0) < 0) {
      setError("Gesetzlicher Urlaub darf nicht negativ sein.");
      return;
    }
    if (parseLocaleNumber(form.additional_vacation_days, 0) < 0) {
      setError("Zusätzlicher Urlaub darf nicht negativ sein.");
      return;
    }
    if (parseLocaleNumber(form.allowance_per_hour, 0) < 0) {
      setError("Zulage pro Stunde darf nicht negativ sein.");
      return;
    }
    if (parseLocaleNumber(form.vma_tax_free_per_day, 0) < 0) {
      setError("Steuerfreie VMA pro Tag darf nicht negativ sein.");
      return;
    }
    if (parseLocaleNumber(form.overnight_tax_free_per_day, 0) < 0) {
      setError("Steuerfreie Übernachtungspauschale pro Tag darf nicht negativ sein.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave({
        first_name: form.first_name,
        last_name: form.last_name,
        birth_date: form.birth_date || null,
        birth_name: form.birth_name,
        birth_place: form.birth_place,
        birth_country: form.birth_country,
        street: form.street,
        house_number: form.house_number,
        postal_code: form.postal_code,
        city: form.city,
        email: form.email,
        phone: form.phone,
        department: form.department,
        position: form.position,
        work_area: form.work_area,
        employment_type: form.employment_type,
        weekly_hours: parseLocaleNumber(form.weekly_hours, 0),
        statutory_vacation_days: parseLocaleNumber(form.statutory_vacation_days, 0),
        additional_vacation_days: parseLocaleNumber(form.additional_vacation_days, 0),
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        color_idx: form.color_idx,
        iban: normalizedIban,
        compensation_type: form.compensation_type === 'salary' ? 'salary' : 'hourly',
        hourly_wage: parseLocaleNumber(form.hourly_wage, 0),
        monthly_salary: parseLocaleNumber(form.monthly_salary, 0),
        allowance_per_hour: parseLocaleNumber(form.allowance_per_hour, 0),
        vma_tax_free_per_day: parseLocaleNumber(form.vma_tax_free_per_day, 0),
        overnight_tax_free_per_day: parseLocaleNumber(form.overnight_tax_free_per_day, 0),
        bank_name: form.bank_name,
        bank_account_holder: form.bank_account_holder,
        social_security_number: form.social_security_number,
        personal_number: form.personal_number,
        tax_id: normalizedTaxId,
        health_insurance_name: form.health_insurance_name,
        tax_class: normalizedTaxClass,
        religion_code: form.religion_code,
        child_allowance_count: parseLocaleNumber(form.child_allowance_count, 0),
        marital_status: form.marital_status,
        nationality_code: form.nationality_code.trim().toUpperCase(),
        severe_disability: form.severe_disability,
        emergency_contact_name: form.emergency_contact_name,
        emergency_contact_relation: form.emergency_contact_relation,
        emergency_contact_phone: form.emergency_contact_phone,
        driving_license_classes: form.driving_license_classes
          .split(',')
          .map((x) => x.trim())
          .filter(Boolean),
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Mitarbeiter konnte nicht angelegt werden.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[120] flex justify-end">
      <button type="button" className="absolute inset-0 bg-black/25" onClick={onClose} />
      <div className="relative h-full w-full max-w-[480px] bg-app-surface shadow-2xl flex flex-col">
        <div className="px-6 py-5 border-b border-app-border flex items-start justify-between shrink-0">
          <div>
            <h3 className="text-base font-semibold text-app-text">Neuen Mitarbeiter anlegen</h3>
            <p className="mt-0.5 text-xs text-app-text-subtle">Schritt {step + 1} von {steps.length}</p>
          </div>
          <button type="button" onClick={onClose} className="grid h-7 w-7 place-items-center rounded-md bg-app-surface-muted text-app-text-subtle hover:bg-app-hover">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex shrink-0 border-b border-app-border px-6 py-3">
          {steps.map((label, idx) => (
            <div key={label} className={`flex items-center ${idx < steps.length - 1 ? 'flex-1' : ''}`}>
              <div className="flex items-center gap-2">
                <span
                  className={`grid h-6 w-6 shrink-0 place-items-center rounded-full text-xs font-bold transition ${
                    idx < step
                      ? 'bg-blue-600 text-white'
                      : idx === step
                        ? 'border-2 border-blue-600 bg-blue-50 text-blue-700'
                        : 'bg-app-surface-muted text-app-text-subtle'
                  }`}
                >
                  {idx < step ? <Check className="h-3 w-3" /> : idx + 1}
                </span>
                <span className={`whitespace-nowrap text-xs ${idx === step ? 'font-semibold text-app-text' : 'text-app-text-subtle'}`}>{label}</span>
              </div>
              {idx < steps.length - 1 && <span className={`mx-3 h-px flex-1 ${idx < step ? 'bg-blue-600' : 'bg-app-border'}`} />}
            </div>
          ))}
        </div>
        <div className="app-scrollbar flex-1 overflow-y-auto overscroll-contain px-6 py-6">
          {step === 0 && (
            <div className="space-y-5">
              <div className="flex items-center gap-3 rounded-lg border border-app-border bg-app-surface-muted px-4 py-4">
                <div className={`grid h-12 w-12 shrink-0 place-items-center rounded-full text-base font-bold ${selectedColor.bg} ${selectedColor.text}`}>
                  {displayName ? getInitials({ first_name: form.first_name, last_name: form.last_name }) : '?'}
                </div>
                <div className="min-w-0">
                  <p className={`text-sm font-semibold ${displayName ? 'text-app-text' : 'text-app-text-subtle'}`}>{displayName || 'Vorname Nachname'}</p>
                  <p className="mt-1 text-xs text-app-text-muted">Persönliche Daten und Kontaktdaten</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <LabeledInput label="Vorname" value={form.first_name} onChange={(v) => setForm((f) => ({ ...f, first_name: v }))} placeholder="Vorname" required />
                <LabeledInput label="Nachname" value={form.last_name} onChange={(v) => setForm((f) => ({ ...f, last_name: v }))} placeholder="Nachname" required />
              </div>
              <LabeledInput label="Geburtsdatum" type="date" value={form.birth_date} onChange={(v) => setForm((f) => ({ ...f, birth_date: v }))} />
              <LabeledInput label="Geburtsname" value={form.birth_name} onChange={(v) => setForm((f) => ({ ...f, birth_name: v }))} />
              <div className="grid grid-cols-2 gap-3">
                <LabeledInput label="Geburtsort" value={form.birth_place} onChange={(v) => setForm((f) => ({ ...f, birth_place: v }))} />
                <LabeledInput label="Geburtsland" value={form.birth_country} onChange={(v) => setForm((f) => ({ ...f, birth_country: v }))} />
              </div>
              <div className="grid grid-cols-[1fr_96px] gap-3">
                <LabeledInput label="Straße" value={form.street} onChange={(v) => setForm((f) => ({ ...f, street: v }))} placeholder="Straße" />
                <LabeledInput label="Hausnr." value={form.house_number} onChange={(v) => setForm((f) => ({ ...f, house_number: v }))} placeholder="Nr." />
              </div>
              <div className="grid grid-cols-[96px_1fr] gap-3">
                <LabeledInput label="PLZ" value={form.postal_code} onChange={(v) => setForm((f) => ({ ...f, postal_code: v }))} placeholder="PLZ" />
                <LabeledInput label="Ort" value={form.city} onChange={(v) => setForm((f) => ({ ...f, city: v }))} placeholder="Ort" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <LabeledInput label="Telefon (für App-Zugang)" type="tel" value={form.phone} onChange={(v) => setForm((f) => ({ ...f, phone: v }))} placeholder="0170…" />
                <LabeledInput label="E-Mail (optional)" type="email" value={form.email} onChange={(v) => setForm((f) => ({ ...f, email: v }))} placeholder="name@firma.de" />
              </div>
            </div>
          )}
          {step === 1 && (
            <div className="space-y-5">
              <div className="space-y-3 rounded-lg border border-app-border bg-app-surface p-4">
                <div>
                  <p className="text-sm font-semibold text-app-text">Berufliches</p>
                  <p className="mt-0.5 text-xs text-app-text-subtle">Rolle, Abteilung und Beschäftigungszeitraum.</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <LabeledInput
                    label="Personalnummer"
                    value={form.personal_number}
                    onChange={(v) => setForm((f) => ({ ...f, personal_number: v }))}
                  />
                  <LabeledInput label="Abteilung" value={form.department} onChange={(v) => setForm((f) => ({ ...f, department: v }))} />
                  <LabeledInput label="Position" value={form.position} onChange={(v) => setForm((f) => ({ ...f, position: v }))} />
                  <label className="block">
                    <span className="text-xs font-medium text-app-text-muted">Arbeitsbereich</span>
                    <AppSelect
                      value={form.work_area}
                      onChange={(value) => setForm((f) => ({ ...f, work_area: value }))}
                      options={workAreaSelectOptions(canAccessInternal)}
                      className="mt-1"
                      size="md"
                      triggerClassName={FORM_SELECT_TRIGGER_CLASS}
                    />
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="text-xs font-medium text-app-text-muted">Beschäftigungsart</span>
                    <AppSelect
                      value={form.employment_type}
                      onChange={(value) => setForm((f) => ({ ...f, employment_type: value }))}
                      options={EMPLOYMENT_TYPE_OPTIONS}
                      className="mt-1"
                      size="md"
                      triggerClassName={FORM_SELECT_TRIGGER_CLASS}
                    />
                  </label>
                  <LabeledInput
                    label="Wöchentliche Arbeitsstunden"
                    type="number"
                    value={form.weekly_hours}
                    onChange={(v) => setForm((f) => ({ ...f, weekly_hours: v }))}
                    placeholder="z. B. 40"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <LabeledInput
                    label="Gesetzlicher Urlaub"
                    type="number"
                    value={form.statutory_vacation_days}
                    onChange={(v) => setForm((f) => ({ ...f, statutory_vacation_days: v }))}
                    placeholder="z. B. 20"
                  />
                  <LabeledInput
                    label="Zusätzlicher Urlaub"
                    type="number"
                    value={form.additional_vacation_days}
                    onChange={(v) => setForm((f) => ({ ...f, additional_vacation_days: v }))}
                    placeholder="z. B. 5"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <LabeledInput label="Eintrittsdatum" type="date" value={form.start_date} onChange={(v) => setForm((f) => ({ ...f, start_date: v }))} />
                  <LabeledInput label="Austrittsdatum" type="date" value={form.end_date} onChange={(v) => setForm((f) => ({ ...f, end_date: v }))} />
                </div>
                <LabeledInput
                  label="Führerscheinklassen"
                  value={form.driving_license_classes}
                  placeholder="z. B. B, BE"
                  onChange={(v) => setForm((f) => ({ ...f, driving_license_classes: v }))}
                />
              </div>

              <div className="space-y-3 rounded-lg border border-app-border bg-app-surface p-4">
                <div>
                  <p className="text-sm font-semibold text-app-text">Finanzen</p>
                  <p className="mt-0.5 text-xs text-app-text-subtle">Bankverbindung und steuerliche Angaben.</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <LabeledInput
                    label="Kontoinhaber"
                    value={form.bank_account_holder}
                    onChange={(v) => setForm((f) => ({ ...f, bank_account_holder: v }))}
                  />
                  <LabeledInput
                    label="IBAN"
                    value={form.iban}
                    invalid={showIbanError}
                    onChange={(v) => setForm((f) => ({ ...f, iban: normalizeIban(v) }))}
                    placeholder="DE00 0000 0000 0000 0000 00"
                  />
                  <LabeledInput label="Bank" value={form.bank_name} onChange={(v) => setForm((f) => ({ ...f, bank_name: v }))} />
                </div>
                <LabeledInput
                  label="Krankenkasse"
                  value={form.health_insurance_name}
                  onChange={(v) => setForm((f) => ({ ...f, health_insurance_name: v }))}
                />
                <div className="grid grid-cols-2 gap-3">
                  <LabeledInput label="Sozialversicherungsnr." value={form.social_security_number} onChange={(v) => setForm((f) => ({ ...f, social_security_number: v }))} />
                  <LabeledInput
                    label="Steuer-ID"
                    value={form.tax_id}
                    inputMode="numeric"
                    maxLength={11}
                    invalid={showTaxIdError}
                    onChange={(v) => setForm((f) => ({ ...f, tax_id: normalizeTaxId(v) }))}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="text-xs font-medium text-app-text-muted">Steuerklasse</span>
                    <AppSelect
                      value={form.tax_class}
                      onChange={(value) => setForm((f) => ({ ...f, tax_class: value }))}
                      options={TAX_CLASS_OPTIONS}
                      className="mt-1"
                      size="md"
                      triggerClassName={FORM_SELECT_TRIGGER_CLASS}
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs font-medium text-app-text-muted">Konfession</span>
                    <AppSelect
                      value={form.religion_code}
                      onChange={(value) => setForm((f) => ({ ...f, religion_code: value }))}
                      options={RELIGION_OPTIONS}
                      className="mt-1"
                      size="md"
                      triggerClassName={FORM_SELECT_TRIGGER_CLASS}
                    />
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <LabeledInput
                    label="Kinderfreibetrag"
                    type="number"
                    value={form.child_allowance_count}
                    onChange={(v) => setForm((f) => ({ ...f, child_allowance_count: v }))}
                    placeholder="z. B. 0.5"
                  />
                  <label className="block">
                    <span className="text-xs font-medium text-app-text-muted">Familienstand</span>
                    <AppSelect
                      value={form.marital_status}
                      onChange={(value) => setForm((f) => ({ ...f, marital_status: value }))}
                      options={MARITAL_STATUS_OPTIONS}
                      className="mt-1"
                      size="md"
                      triggerClassName={FORM_SELECT_TRIGGER_CLASS}
                    />
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <LabeledInput
                    label="Staatsangehörigkeit"
                    value={form.nationality_code}
                    onChange={(v) => setForm((f) => ({ ...f, nationality_code: v.toUpperCase().slice(0, 2) }))}
                    placeholder="DE / PL"
                  />
                  <label className="block">
                    <span className="text-xs font-medium text-app-text-muted">Schwerbehinderung</span>
                    <AppSelect
                      value={String(form.severe_disability)}
                      onChange={(value) => setForm((f) => ({ ...f, severe_disability: value === 'true' }))}
                      options={YES_NO_OPTIONS}
                      className="mt-1"
                      size="md"
                      triggerClassName={FORM_SELECT_TRIGGER_CLASS}
                    />
                  </label>
                </div>
                <div className="space-y-3">
                  <div>
                    <span className="text-xs font-medium text-app-text-muted">Vergütungsart</span>
                    <div className="mt-1 inline-flex rounded-md border border-app-border bg-app-surface-muted p-0.5">
                      {COMPENSATION_TYPE_OPTIONS.map((opt) => {
                        const active = form.compensation_type === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() =>
                              setForm((f) => ({
                                ...f,
                                compensation_type: opt.value as EmployeeCompensationType,
                              }))
                            }
                            className={`rounded px-3 py-1.5 text-xs font-semibold transition ${
                              active
                                ? 'bg-app-surface text-app-text shadow-sm'
                                : 'text-app-text-subtle hover:text-app-text-muted'
                            }`}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {form.compensation_type === 'salary' ? (
                    <LabeledInput
                      label="Monatliches Fixum"
                      type="number"
                      value={form.monthly_salary}
                      onChange={(v) => setForm((f) => ({ ...f, monthly_salary: v }))}
                      placeholder="z. B. 3500"
                    />
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      <LabeledInput
                        label="Stundenlohn"
                        type="number"
                        value={form.hourly_wage}
                        onChange={(v) => setForm((f) => ({ ...f, hourly_wage: v }))}
                        placeholder="z. B. 18.50"
                      />
                      <LabeledInput
                        label="Zulage pro Stunde"
                        type="number"
                        value={form.allowance_per_hour}
                        onChange={(v) => setForm((f) => ({ ...f, allowance_per_hour: v }))}
                      />
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <LabeledInput
                    label="Steuerfreie VMA pro Tag"
                    type="number"
                    value={form.vma_tax_free_per_day}
                    onChange={(v) => setForm((f) => ({ ...f, vma_tax_free_per_day: v }))}
                  />
                  <LabeledInput
                    label="Steuerfreie ÜN-Pauschale/Tag"
                    type="number"
                    value={form.overnight_tax_free_per_day}
                    onChange={(v) => setForm((f) => ({ ...f, overnight_tax_free_per_day: v }))}
                  />
                </div>
              </div>

              <div className="space-y-3 rounded-lg border border-app-border bg-app-surface p-4">
                <div>
                  <p className="text-sm font-semibold text-app-text">Notfallkontakt</p>
                  <p className="mt-0.5 text-xs text-app-text-subtle">Kontaktperson für dringende Fälle.</p>
                </div>
                <LabeledInput label="Name" value={form.emergency_contact_name} onChange={(v) => setForm((f) => ({ ...f, emergency_contact_name: v }))} />
                <LabeledInput label="Beziehung" value={form.emergency_contact_relation} onChange={(v) => setForm((f) => ({ ...f, emergency_contact_relation: v }))} placeholder="z. B. Ehepartner, Elternteil" />
                <LabeledInput label="Telefon" type="tel" value={form.emergency_contact_phone} onChange={(v) => setForm((f) => ({ ...f, emergency_contact_phone: v }))} />
              </div>
            </div>
          )}
          {step === 2 && (
            <div className="space-y-4">
              <div className="rounded-lg border border-app-border bg-app-surface-muted p-4">
                <div className="mb-3 flex items-center gap-3">
                  <div className={`grid h-11 w-11 place-items-center rounded-full text-sm font-bold ${selectedColor.bg} ${selectedColor.text}`}>
                    {getInitials({ first_name: form.first_name, last_name: form.last_name })}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-app-text">{displayName || 'Unbenannter Mitarbeiter'}</p>
                    <p className="mt-0.5 text-xs text-app-text-muted">{[form.position, form.department].filter(Boolean).join(' · ') || 'Position / Abteilung fehlt'}</p>
                  </div>
                </div>
                {summaryRows.map(([label, value]) => (
                  <div key={label} className="flex justify-between border-t border-app-border py-1.5 text-xs">
                    <span className="text-app-text-subtle">{label}</span>
                    <span className="max-w-[55%] text-right font-medium text-app-text break-words">{value}</span>
                  </div>
                ))}
              </div>
              {showInviteHint ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs leading-5 text-amber-800">
                  Nach dem Speichern kannst du dem Mitarbeiter optional über <strong>Zugang einladen</strong> Zugriff zur App geben.
                  Dafür wird die Telefonnummer benötigt - der Techniker meldet sich in der App per SMS-Code an.
                </div>
              ) : null}
            </div>
          )}
          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>}
        </div>
        <div className="flex shrink-0 justify-between gap-3 border-t border-app-border px-6 py-4">
          <button
            type="button"
            onClick={() => (step === 0 ? onClose() : setStep((s) => s - 1))}
            className="inline-flex h-9 items-center gap-1.5 rounded-md border border-app-border bg-app-surface px-4 text-sm font-medium text-app-text-muted hover:bg-app-hover"
          >
            {step > 0 && <ChevronLeft className="h-3.5 w-3.5" />}
            {step === 0 ? 'Abbrechen' : 'Zurück'}
          </button>
          <button
            type="button"
            disabled={!canContinue || saving}
            onClick={() => (step < 2 ? setStep((s) => s + 1) : void submit())}
            className={`inline-flex h-9 items-center gap-1.5 rounded-md px-5 text-sm font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:bg-app-surface-muted disabled:text-app-text-subtle ${
              step === 2 ? 'bg-pipedrive-green hover:bg-[#218838]' : 'bg-pipedrive-blue hover:bg-blue-700'
            }`}
          >
            {step < 2 ? (
              <>
                Weiter <ChevronRight className="h-3.5 w-3.5" />
              </>
            ) : saving ? (
              'Speichert…'
            ) : (
              'Mitarbeiter anlegen'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function LabeledInput({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  required = false,
  inputMode,
  maxLength,
  invalid = false,
  disabled = false,
  min,
  step,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  maxLength?: number;
  invalid?: boolean;
  disabled?: boolean;
  min?: number;
  step?: number | string;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-app-text-muted">
        {label}
        {required && <span className="text-red-500"> *</span>}
      </span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        inputMode={inputMode}
        maxLength={maxLength}
        disabled={disabled}
        min={min}
        step={step}
        onChange={(e) => onChange(e.target.value)}
        className={`mt-1 h-9 w-full rounded-md border px-3 text-sm outline-none placeholder:text-app-text-subtle ${
          disabled
            ? 'border-app-border bg-app-surface-muted text-app-text-subtle'
            : invalid
              ? 'border-red-400 bg-app-surface text-app-text focus:border-red-500'
              : 'border-app-border bg-app-surface text-app-text focus:border-pipedrive-blue focus:ring-2 focus:ring-pipedrive-blue/20'
        }`}
      />
    </label>
  );
}

export function EmployeesPage() {
  const navigate = useNavigate();
  const { can, loaded: capabilitiesLoaded } = useCapabilities();
  const canInviteUser = can('web.einstellungen.invite');
  const canAccessInternal = can('web.hr.internal');
  const [myMembershipRole, setMyMembershipRole] = React.useState<PlantafelMembershipRole>('member');
  const canManageInvites = myMembershipRole === 'owner' || myMembershipRole === 'admin';
  const [searchParams, setSearchParams] = useSearchParams();
  const { instanceId, employeeId: employeeIdParam, tab: tabParam, overviewTab: overviewTabParam } = useParams<{
    instanceId: string;
    employeeId?: string;
    tab?: string;
    overviewTab?: string;
  }>();
  const isOverviewRoute = Boolean(overviewTabParam);
  const overviewTab = overviewTabFromParam(overviewTabParam);
  const overviewPeriod = useEmployeePeriodQuery('month');
  const [employees, setEmployees] = React.useState<Employee[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [search, setSearch] = React.useState('');
  const [currentUserId, setCurrentUserId] = React.useState<string | null>(null);
  const [workAreaFilter, setWorkAreaFilter] = React.useState<EmployeeWorkAreaFilter>(
    () => readInitialEmployeeWorkAreaFilter(),
  );
  const workAreaFilterRef = React.useRef(workAreaFilter);
  const workAreaFilterTouchedRef = React.useRef(false);
  const [inactiveOpen, setInactiveOpen] = React.useState(false);
  const activeTab: TabId = isEmployeeDetailTab(tabParam) ? tabParam : 'profile';
  const [showWizard, setShowWizard] = React.useState(false);
  const [savingProfile, setSavingProfile] = React.useState(false);
  const [editMode, setEditMode] = React.useState(false);
  // Erhöht nach jedem Speichern, damit der Änderungsverlauf im Stammdaten-Tab neu lädt.
  const [masterDataHistoryKey, setMasterDataHistoryKey] = React.useState(0);
  const [profileDraft, setProfileDraft] = React.useState<Employee | null>(null);
  const [inviting, setInviting] = React.useState(false);
  const [inviteModalOpen, setInviteModalOpen] = React.useState(false);
  const [inviteModalError, setInviteModalError] = React.useState<string | null>(null);
  const [rolePresets, setRolePresets] = React.useState<RolePresetOption[]>([]);
  const [documents, setDocuments] = React.useState<EmployeeDocument[]>([]);
  const [documentsLoading, setDocumentsLoading] = React.useState(false);
  const [documentTypes, setDocumentTypes] = React.useState<EmployeeDocumentTypeRow[]>(() =>
    fallbackEmployeeDocumentTypes(),
  );
  const [docFilter, setDocFilter] = React.useState<DocumentFilter>('all');
  const [docUploadModalOpen, setDocUploadModalOpen] = React.useState(false);
  const [pendingFile, setPendingFile] = React.useState<File | null>(null);
  const [pendingCategory, setPendingCategory] = React.useState(EMPLOYEE_DOCUMENT_TYPE_OTHER_KEY);
  const [uploadingDoc, setUploadingDoc] = React.useState(false);
  const [isDocDragging, setIsDocDragging] = React.useState(false);
  const [pendingDeleteDoc, setPendingDeleteDoc] = React.useState<EmployeeDocument | null>(null);
  const [deletingDocId, setDeletingDocId] = React.useState<string | null>(null);
  const [docEditModalOpen, setDocEditModalOpen] = React.useState(false);
  const [editingDoc, setEditingDoc] = React.useState<EmployeeDocument | null>(null);
  const [docEditName, setDocEditName] = React.useState('');
  const [docEditCategory, setDocEditCategory] = React.useState(EMPLOYEE_DOCUMENT_TYPE_OTHER_KEY);
  const [savingDocEdit, setSavingDocEdit] = React.useState(false);
  const [previewDocument, setPreviewDocument] = React.useState<InboxAttachmentPreviewTarget | null>(null);
  const [pendingDeleteEmployee, setPendingDeleteEmployee] = React.useState<Employee | null>(null);
  const [deleteImpact, setDeleteImpact] = React.useState<{
    documents: number;
    timeEntries: number;
    changeRequests: number;
    payrollEntries: number;
    payrollRules: number;
    activityAssignees: number;
    primaryActivities: number;
    hasLinkedUser: boolean;
  } | null>(null);
  const [deleteImpactLoading, setDeleteImpactLoading] = React.useState(false);
  const [deletingEmployeeId, setDeletingEmployeeId] = React.useState<string | null>(null);
  const [timeAnchor, setTimeAnchor] = React.useState<Date>(() => {
    const fromUrl = parseMonthQueryParam(searchParams.get('month'));
    if (fromUrl) return fromUrl;
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [timeEntries, setTimeEntries] = React.useState<TimeEntry[]>([]);
  const [holidays, setHolidays] = React.useState<Holiday[]>([]);
  const [timeLoading, setTimeLoading] = React.useState(false);
  const [profileSaveAttempted, setProfileSaveAttempted] = React.useState(false);
  const [entryModalDate, setEntryModalDate] = React.useState<string | null>(null);
  const [entryDraft, setEntryDraft] = React.useState<{
    entry_type: TimeEntry['entry_type'];
    start_time: string;
    end_time: string;
    break_minutes: string;
    note: string;
  }>({ entry_type: 'work', start_time: '', end_time: '', break_minutes: '0', note: '' });
  const [savingTimeEntry, setSavingTimeEntry] = React.useState(false);
  const [deletingTimeEntry, setDeletingTimeEntry] = React.useState(false);
  const [payrollPeriodStatusMap, setPayrollPeriodStatusMap] = React.useState<
    Map<string, PayrollPeriodStatus>
  >(() => new Map());
  const [isPayrollApprover, setIsPayrollApprover] = React.useState(false);
  const [entryModalNotice, setEntryModalNotice] = React.useState<string | null>(null);
  // Set when saving the day stamp would overlap appointment/project time (409 overlap).
  const [entryOverlapWarning, setEntryOverlapWarning] = React.useState<string | null>(null);
  // Appointment/project time edited in the day dialog; saved on that entry, its link stays.
  const [entryModalLinkedEntry, setEntryModalLinkedEntry] = React.useState<TimeEntry | null>(null);
  // „+ Eintrag“: always a new, empty day entry — never opens an existing appointment/project time.
  const [entryModalFresh, setEntryModalFresh] = React.useState(false);
  const [pendingChangeRequests, setPendingChangeRequests] = React.useState<
    Map<string, PendingChangeRequestInfo>
  >(() => new Map());
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const dropFileInputRef = React.useRef<HTMLInputElement | null>(null);
  const calendarCardRef = React.useRef<HTMLDivElement | null>(null);
  const [calendarCardHeight, setCalendarCardHeight] = React.useState<number | null>(null);

  const selected = React.useMemo(
    () => employees.find((e) => e.id === selectedId) ?? null,
    [employees, selectedId]
  );

  const employeeDocumentTitle = React.useMemo(() => {
    if (!selected || !employeeIdParam || String(selected.id) !== String(employeeIdParam)) return null;
    const name = [selected.first_name, selected.last_name].filter(Boolean).join(' ').trim();
    return name || null;
  }, [selected, employeeIdParam]);
  useDocumentTitle(employeeDocumentTitle);

  const navigateToEmployee = React.useCallback(
    (employeeId: string, tab: TabId, replace = false) => {
      if (!instanceId) return;
      const query =
        tab === 'time-tracking' ? { month: monthQueryParamFromDate(timeAnchor) } : undefined;
      navigate(employeeDetailPath(instanceId, employeeId, tab, query), { replace });
    },
    [instanceId, navigate, timeAnchor],
  );

  const navigateToOverview = React.useCallback(
    (tab: typeof overviewTab = 'uebersicht', replace = false) => {
      if (!instanceId) return;
      const path = employeesOverviewPath(instanceId, tab, {
        from: overviewPeriod.from,
        to: overviewPeriod.to,
      });
      navigate(path, { replace });
    },
    [instanceId, navigate, overviewPeriod.from, overviewPeriod.to],
  );

  const filteredEmployees = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = q
      ? employees.filter((emp) =>
          [emp.first_name, emp.last_name, emp.position, emp.department]
            .filter(Boolean)
            .join(' ')
            .toLowerCase()
            .includes(q)
        )
      : employees;
    const areaFiltered = rows.filter((emp) => matchesEmployeeWorkAreaFilter(emp.work_area, workAreaFilter));
    return [...areaFiltered].sort((a, b) =>
      comparePlantafelEmployees(
        { name: employeeSortName(a), membership_role: a.membership_role },
        { name: employeeSortName(b), membership_role: b.membership_role },
      ),
    );
  }, [employees, search, workAreaFilter]);

  const activeEmployees = React.useMemo(
    () => filteredEmployees.filter((emp) => !emp.is_archived),
    [filteredEmployees]
  );
  const inactiveEmployees = React.useMemo(
    () => filteredEmployees.filter((emp) => emp.is_archived),
    [filteredEmployees]
  );

  const loadEmployees = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [res, presetsRes] = await Promise.all([
        fetch('/api/employees'),
        fetch('/api/me/role-presets', { credentials: 'include' }),
      ]);
      const data = (await res.json()) as { items?: Employee[]; error?: string };
      if (!res.ok) throw new Error(data.error || 'Mitarbeiter konnten nicht geladen werden.');
      const rows = Array.isArray(data.items) ? data.items : [];
      setEmployees(rows);
      if (presetsRes.ok) {
        const presetData = (await presetsRes.json()) as { presets?: RolePresetOption[] };
        setRolePresets(Array.isArray(presetData.presets) ? presetData.presets : []);
      }
    } catch (e) {
      console.error('Failed to load employees', e);
      setError(e instanceof Error ? e.message : 'Mitarbeiter konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDocuments = React.useCallback(async (employeeId: string) => {
    setDocumentsLoading(true);
    try {
      const res = await fetch(`/api/employees/${encodeURIComponent(employeeId)}/documents`);
      const data = (await res.json()) as { items?: EmployeeDocument[] };
      if (!res.ok) throw new Error((data as any).error || 'Dokumente konnten nicht geladen werden.');
      setDocuments(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      console.error('Failed to load employee documents', e);
      setDocuments([]);
    } finally {
      setDocumentsLoading(false);
    }
  }, []);

  const loadTimeData = React.useCallback(
    async (employeeId: string, anchor: Date) => {
      setTimeLoading(true);
      try {
        const year = anchor.getFullYear();
        const fromStr = yearBounds(year).from;
        const toStr = yearBounds(year).to;
        const [entriesRes, holidaysRes, periodsRes, meRes, requestsRes] = await Promise.all([
          fetch(`/api/employees/${encodeURIComponent(employeeId)}/time-entries?from=${encodeURIComponent(fromStr)}&to=${encodeURIComponent(toStr)}`),
          fetch(`/api/holidays?year=${anchor.getFullYear()}`),
          fetch('/api/employees/payroll-periods', { credentials: 'include' }),
          fetch('/api/me', { credentials: 'include' }),
          fetch('/api/employees/time-entry-change-requests?status=pending', { credentials: 'include' }),
        ]);
        const entriesJson = (await entriesRes.json()) as { items?: TimeEntry[] };
        const holidaysJson = (await holidaysRes.json()) as { items?: Holiday[] };
        const periodsJson = (await periodsRes.json()) as { items?: PayrollPeriodListItem[] };
        if (!entriesRes.ok) throw new Error((entriesJson as any).error || 'Zeiterfassung konnte nicht geladen werden.');
        setTimeEntries(Array.isArray(entriesJson.items) ? entriesJson.items : []);
        setHolidays(Array.isArray(holidaysJson.items) ? holidaysJson.items : []);
        if (periodsRes.ok) {
          const items = Array.isArray(periodsJson.items) ? periodsJson.items : [];
          setPayrollPeriodStatusMap(buildPayrollPeriodStatusMap(items));
        }
        if (meRes.ok) {
          const me = (await meRes.json()) as { is_payroll_approver?: boolean };
          setIsPayrollApprover(Boolean(me.is_payroll_approver));
        }
        if (requestsRes.ok) {
          const reqJson = (await requestsRes.json()) as {
            items?: Array<{
              employee_id: string;
              entry_date: string;
              action: 'upsert' | 'delete';
              proposed_payload?: Record<string, unknown> | null;
            }>;
          };
          setPendingChangeRequests(
            buildPendingChangeRequestMap(
              Array.isArray(reqJson.items) ? reqJson.items : [],
              employeeId,
            ),
          );
        }
      } catch (e) {
        console.error('Failed to load time tracking', e);
        setTimeEntries([]);
        setHolidays([]);
      } finally {
        setTimeLoading(false);
      }
    },
    []
  );

  const loadDocumentTypes = React.useCallback(async () => {
    try {
      const res = await fetch('/api/settings/employee-document-types', { credentials: 'include' });
      if (!res.ok) return;
      const parsed = parseEmployeeDocumentTypeRows(await res.json());
      if (parsed.length > 0) setDocumentTypes(parsed);
    } catch {
      /* keep seeded fallback */
    }
  }, []);

  React.useEffect(() => {
    void loadEmployees();
  }, [loadEmployees]);

  React.useEffect(() => {
    void loadDocumentTypes();
  }, [loadDocumentTypes]);

  React.useEffect(() => {
    workAreaFilterRef.current = workAreaFilter;
  }, [workAreaFilter]);

  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch('/api/me', { credentials: 'include' });
        if (!res.ok) return;
        const data = (await res.json()) as { user?: { id?: string }; role?: PlantafelMembershipRole };
        const userId = String(data.user?.id ?? '').trim();
        if (!userId || cancelled) return;
        setCurrentUserId(userId);
        if (data.role === 'owner' || data.role === 'admin' || data.role === 'member') {
          setMyMembershipRole(data.role);
        }
        if (workAreaFilterTouchedRef.current) {
          writeEmployeeWorkAreaFilter(userId, workAreaFilterRef.current);
          return;
        }
        const stored = sanitizeWorkAreaFilter(
          readEmployeeWorkAreaFilter(userId),
          capabilitiesLoaded ? canAccessInternal : true,
        );
        // Only update when this user's preference differs (e.g. another account on same device).
        if (stored !== workAreaFilterRef.current) {
          setWorkAreaFilter(stored);
        }
        // Always refresh lastFilter/lastUserId so the next reload hydrates this account.
        writeEmployeeWorkAreaFilter(userId, stored);
      } catch {
        // ignore — filter already hydrated from lastFilter
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleWorkAreaFilterChange = React.useCallback(
    (next: EmployeeWorkAreaFilter) => {
      const sanitized = sanitizeWorkAreaFilter(next, canAccessInternal);
      workAreaFilterTouchedRef.current = true;
      workAreaFilterRef.current = sanitized;
      setWorkAreaFilter(sanitized);
      writeEmployeeWorkAreaFilter(currentUserId, sanitized);
    },
    [currentUserId, canAccessInternal],
  );

  React.useEffect(() => {
    if (!capabilitiesLoaded || canAccessInternal) return;
    setWorkAreaFilter((current) => {
      const sanitized = sanitizeWorkAreaFilter(current, false);
      if (sanitized === current) return current;
      workAreaFilterRef.current = sanitized;
      writeEmployeeWorkAreaFilter(currentUserId, sanitized);
      return sanitized;
    });
  }, [canAccessInternal, capabilitiesLoaded, currentUserId]);

  React.useEffect(() => {
    if (isOverviewRoute) return;

    if (employees.length === 0) {
      setSelectedId(null);
      return;
    }

    const hasParamEmployee = Boolean(employeeIdParam && employees.some((e) => e.id === employeeIdParam));
    if (!employeeIdParam || !hasParamEmployee) {
      navigateToOverview('uebersicht', true);
      return;
    }

    const nextSelectedId = employeeIdParam as string;
    if (selectedId !== nextSelectedId) {
      setSelectedId(nextSelectedId);
    }

    const nextTab: TabId = isEmployeeDetailTab(tabParam) ? tabParam : 'profile';
    if (!isEmployeeDetailTab(tabParam)) {
      navigateToEmployee(nextSelectedId, nextTab, true);
    }
  }, [employees, employeeIdParam, tabParam, selectedId, navigateToEmployee, navigateToOverview, isOverviewRoute]);

  React.useEffect(() => {
    if (!selected) {
      setDocuments([]);
      setTimeEntries([]);
      setHolidays([]);
      return;
    }
    if (activeTab === 'documents') void loadDocuments(selected.id);
    if (activeTab === 'time-tracking') void loadTimeData(selected.id, timeAnchor);
  }, [selected, activeTab, loadDocuments, loadTimeData, timeAnchor]);

  React.useEffect(() => {
    setProfileDraft(
      selected
        ? {
            ...selected,
            compensation_type: selected.compensation_type === 'salary' ? 'salary' : 'hourly',
            monthly_salary: Number(selected.monthly_salary) || 0,
          }
        : null,
    );
    setEditMode(false);
    setProfileSaveAttempted(false);
  }, [selectedId, selected]);

  React.useEffect(() => {
    if (!entryModalDate) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setEntryModalDate(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [entryModalDate]);

  React.useEffect(() => {
    if (activeTab !== 'time-tracking') return;
    const node = calendarCardRef.current;
    if (!node) return;

    const updateHeight = () => {
      setCalendarCardHeight(Math.ceil(node.getBoundingClientRect().height));
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(node);
    window.addEventListener('resize', updateHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateHeight);
    };
  }, [activeTab]);

  const saveDraft = async () => {
    if (!selected || !profileDraft) return;
    setProfileSaveAttempted(true);
    if (!String(profileDraft.first_name ?? '').trim() || !String(profileDraft.last_name ?? '').trim()) {
      setError('Vorname und Nachname sind erforderlich.');
      return;
    }
    if (!isValidIban(profileDraft.iban)) {
      setError('Ungültige IBAN.');
      return;
    }
    if (!isValidTaxId(profileDraft.tax_id)) {
      setError('Steuer-ID muss genau 11 Ziffern haben.');
      return;
    }
    const normalizedTaxClass = String(profileDraft.tax_class ?? '').trim();
    const normalizedReligionCode = String(profileDraft.religion_code ?? '').trim();
    const normalizedMaritalStatus = String(profileDraft.marital_status ?? '').trim();
    const normalizedEmploymentType = String(profileDraft.employment_type ?? '').trim();
    const normalizedWorkArea = String(profileDraft.work_area ?? '').trim();
    if (!TAX_CLASS_VALUE_SET.has(normalizedTaxClass)) {
      setError('Ungültige Steuerklasse.');
      return;
    }
    if (!RELIGION_VALUE_SET.has(normalizedReligionCode)) {
      setError('Ungültige Konfession.');
      return;
    }
    if (!MARITAL_STATUS_VALUE_SET.has(normalizedMaritalStatus)) {
      setError('Ungültiger Familienstand.');
      return;
    }
    if (!EMPLOYMENT_TYPE_VALUE_SET.has(normalizedEmploymentType)) {
      setError('Ungültige Beschäftigungsart.');
      return;
    }
    if (!WORK_AREA_VALUE_SET.has(normalizedWorkArea)) {
      setError('Ungültiger Arbeitsbereich.');
      return;
    }
    if (profileDraft.nationality_code && !/^[A-Z]{2}$/.test(String(profileDraft.nationality_code).toUpperCase())) {
      setError('Staatsangehörigkeit muss ein ISO-Ländercode (2 Buchstaben) sein.');
      return;
    }
    if (parseLocaleNumber(profileDraft.child_allowance_count, 0) < 0) {
      setError('Kinderfreibetrag darf nicht negativ sein.');
      return;
    }
    if (parseLocaleNumber(profileDraft.weekly_hours, 0) < 0) {
      setError('Wöchentliche Arbeitsstunden dürfen nicht negativ sein.');
      return;
    }
    if (parseLocaleNumber(profileDraft.hourly_wage, 0) < 0) {
      setError('Stundenlohn darf nicht negativ sein.');
      return;
    }
    if (parseLocaleNumber(profileDraft.monthly_salary, 0) < 0) {
      setError('Monatliches Fixum darf nicht negativ sein.');
      return;
    }
    if (parseLocaleNumber(profileDraft.statutory_vacation_days, 0) < 0) {
      setError('Gesetzlicher Urlaub darf nicht negativ sein.');
      return;
    }
    if (parseLocaleNumber(profileDraft.additional_vacation_days, 0) < 0) {
      setError('Zusätzlicher Urlaub darf nicht negativ sein.');
      return;
    }
    if (parseLocaleNumber(profileDraft.allowance_per_hour, 0) < 0) {
      setError('Zulage pro Stunde darf nicht negativ sein.');
      return;
    }
    if (parseLocaleNumber(profileDraft.vma_tax_free_per_day, 0) < 0) {
      setError('Steuerfreie VMA pro Tag darf nicht negativ sein.');
      return;
    }
    if (parseLocaleNumber(profileDraft.overnight_tax_free_per_day, 0) < 0) {
      setError('Steuerfreie Übernachtungspauschale pro Tag darf nicht negativ sein.');
      return;
    }
    setSavingProfile(true);
    try {
      const res = await fetch(`/api/employees/${encodeURIComponent(selected.id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...profileDraft,
          tax_class: normalizedTaxClass,
          religion_code: normalizedReligionCode,
          marital_status: normalizedMaritalStatus,
          employment_type: normalizedEmploymentType,
          work_area: normalizedWorkArea,
          expected_hours_per_day: parseLocaleNumber(profileDraft.expected_hours_per_day, 0),
          child_allowance_count: parseLocaleNumber(profileDraft.child_allowance_count, 0),
          weekly_hours: parseLocaleNumber(profileDraft.weekly_hours, 0),
          compensation_type: profileDraft.compensation_type === 'salary' ? 'salary' : 'hourly',
          hourly_wage: parseLocaleNumber(profileDraft.hourly_wage, 0),
          monthly_salary: parseLocaleNumber(profileDraft.monthly_salary, 0),
          statutory_vacation_days: parseLocaleNumber(profileDraft.statutory_vacation_days, 0),
          additional_vacation_days: parseLocaleNumber(profileDraft.additional_vacation_days, 0),
          allowance_per_hour: parseLocaleNumber(profileDraft.allowance_per_hour, 0),
          vma_tax_free_per_day: parseLocaleNumber(profileDraft.vma_tax_free_per_day, 0),
          overnight_tax_free_per_day: parseLocaleNumber(profileDraft.overnight_tax_free_per_day, 0),
        }),
      });
      const data = (await res.json()) as { item?: Employee; error?: string };
      if (!res.ok) throw new Error(data.error || 'Mitarbeiter konnte nicht gespeichert werden.');
      const saved = data.item;
      if (saved) {
        setEmployees((prev) => prev.map((row) => (row.id === saved.id ? saved : row)));
      }
      setMasterDataHistoryKey((k) => k + 1);
      setEditMode(false);
    } catch (e) {
      console.error('Failed to save employee', e);
      setError(e instanceof Error ? e.message : 'Mitarbeiter konnte nicht gespeichert werden.');
    } finally {
      setSavingProfile(false);
    }
  };

  const createEmployee = async (payload: Partial<Employee>) => {
    const res = await fetch('/api/employees', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = (await res.json()) as { item?: Employee; error?: string };
    if (!res.ok || !data.item) {
      throw new Error(data.error || 'Mitarbeiter konnte nicht erstellt werden.');
    }
    setEmployees((prev) => [...prev, data.item as Employee]);
    navigateToEmployee(data.item.id, 'profile');
  };

  const openInviteModal = () => {
    if (!canInviteUser || !selected) return;
    setInviteModalError(null);
    setInviteModalOpen(true);
  };

  const closeInviteModal = () => {
    if (inviting) return;
    setInviteModalOpen(false);
    setInviteModalError(null);
  };

  const submitInviteAccess = async (payload: {
    channel: 'email' | 'phone';
    email: string;
    phone: string;
    first_name: string;
    last_name: string;
    full_name: string;
    role: 'member' | 'admin';
    platform_access: 'office_and_field' | 'field_only';
    role_preset_id: string | null;
    capability_overrides: CapabilityMap;
  }) => {
    if (!selected) return;
    setInviting(true);
    setInviteModalError(null);
    setError(null);
    try {
      const res = await fetch(`/api/employees/${encodeURIComponent(selected.id)}/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          role: payload.role,
          role_preset_id: payload.role_preset_id,
          capability_overrides: payload.capability_overrides,
          platform_access: payload.platform_access,
          phone: payload.phone || undefined,
          email: payload.email || undefined,
          full_name: payload.full_name || undefined,
          first_name: payload.first_name || undefined,
          last_name: payload.last_name || undefined,
        }),
      });
      const data = (await res.json()) as { ok?: boolean; error?: string; warning?: string | null; sms_sent?: boolean };
      if (!res.ok) throw new Error(data.error || 'Einladung konnte nicht versendet werden.');
      setInviteModalOpen(false);
      await loadEmployees();
      if (data.warning) {
        setError(
          data.sms_sent === false
            ? `Zugang angelegt, aber SMS fehlgeschlagen: ${data.warning}`
            : data.warning,
        );
      }
    } catch (e) {
      console.error('Failed to invite employee access', e);
      setInviteModalError(e instanceof Error ? e.message : 'Einladung konnte nicht versendet werden.');
    } finally {
      setInviting(false);
    }
  };

  const toggleArchive = async (nextArchived: boolean) => {
    if (!selected) return;
    setSavingProfile(true);
    try {
      const res = await fetch(`/api/employees/${encodeURIComponent(selected.id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_archived: nextArchived }),
      });
      const data = (await res.json()) as { item?: Employee; error?: string };
      if (!res.ok || !data.item) throw new Error(data.error || 'Mitarbeiter konnte nicht aktualisiert werden.');
      setEmployees((prev) => prev.map((row) => (row.id === data.item!.id ? (data.item as Employee) : row)));
      if (nextArchived) setInactiveOpen(true);
    } catch (e) {
      console.error('Failed to archive/unarchive employee', e);
      setError(e instanceof Error ? e.message : 'Mitarbeiter konnte nicht aktualisiert werden.');
    } finally {
      setSavingProfile(false);
    }
  };

  React.useEffect(() => {
    if (!pendingDeleteEmployee) {
      setDeleteImpact(null);
      return;
    }
    let cancelled = false;
    setDeleteImpactLoading(true);
    fetch(`/api/employees/${encodeURIComponent(pendingDeleteEmployee.id)}/delete-impact`, {
      credentials: 'include',
    })
      .then(async (res) => {
        const data = (await res.json()) as {
          impact?: {
            documents: number;
            timeEntries: number;
            changeRequests: number;
            payrollEntries: number;
            payrollRules: number;
            activityAssignees: number;
            primaryActivities: number;
            hasLinkedUser: boolean;
          };
          error?: string;
        };
        if (!res.ok) throw new Error(data.error || 'Impact fehlgeschlagen');
        if (!cancelled) setDeleteImpact(data.impact ?? null);
      })
      .catch((e) => {
        if (!cancelled) {
          console.error(e);
          setDeleteImpact(null);
        }
      })
      .finally(() => {
        if (!cancelled) setDeleteImpactLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pendingDeleteEmployee]);

  const confirmDeleteEmployee = async () => {
    if (!pendingDeleteEmployee) return;
    setDeletingEmployeeId(pendingDeleteEmployee.id);
    try {
      const res = await fetch(`/api/employees/${encodeURIComponent(pendingDeleteEmployee.id)}`, { method: 'DELETE' });
      const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
      if (!res.ok) throw new Error(data.error || 'Mitarbeiter konnte nicht gelöscht werden.');
      setEmployees((prev) => {
        const next = prev.filter((row) => row.id !== pendingDeleteEmployee.id);
        setSelectedId(next[0]?.id ?? null);
        return next;
      });
      setPendingDeleteEmployee(null);
      setEditMode(false);
    } catch (e) {
      console.error('Failed to delete employee', e);
      setError(e instanceof Error ? e.message : 'Mitarbeiter konnte nicht gelöscht werden.');
    } finally {
      setDeletingEmployeeId(null);
    }
  };

  const onSelectUploadFile = (file: File | null) => {
    if (!file) return;
    setPendingFile(file);
    const firstEnabled =
      enabledEmployeeDocumentTypes(documentTypes)[0]?.key ?? EMPLOYEE_DOCUMENT_TYPE_OTHER_KEY;
    setPendingCategory(firstEnabled);
    setDocUploadModalOpen(true);
  };

  const handleDocDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDocDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) onSelectUploadFile(file);
  };

  const handleDocDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDocDragging(true);
  };

  const handleDocDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDocDragging(false);
  };

  const uploadDocument = async () => {
    if (!selected || !pendingFile) return;
    setUploadingDoc(true);
    try {
      const form = new FormData();
      form.append('file', pendingFile);
      form.append('category', pendingCategory);
      const res = await fetch(`/api/employees/${encodeURIComponent(selected.id)}/documents`, {
        method: 'POST',
        body: form,
      });
      const data = (await res.json()) as { item?: EmployeeDocument; error?: string };
      if (!res.ok || !data.item) throw new Error(data.error || 'Upload fehlgeschlagen.');
      setDocuments((prev) => [data.item as EmployeeDocument, ...prev]);
      setDocUploadModalOpen(false);
      setPendingFile(null);
    } catch (e) {
      console.error('Failed to upload employee document', e);
      setError(e instanceof Error ? e.message : 'Upload fehlgeschlagen.');
    } finally {
      setUploadingDoc(false);
    }
  };

  const deleteDocument = async (doc: EmployeeDocument) => {
    setPendingDeleteDoc(doc);
  };

  const confirmDeleteDocument = async () => {
    if (!selected || !pendingDeleteDoc) return;
    setDeletingDocId(pendingDeleteDoc.id);
    try {
      const res = await fetch(`/api/employees/${encodeURIComponent(selected.id)}/documents/${encodeURIComponent(pendingDeleteDoc.id)}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('Dokument konnte nicht gelöscht werden.');
      setDocuments((prev) => prev.filter((x) => x.id !== pendingDeleteDoc.id));
      setPendingDeleteDoc(null);
    } catch (e) {
      console.error('Failed to delete employee document', e);
      setError('Dokument konnte nicht gelöscht werden.');
    } finally {
      setDeletingDocId(null);
    }
  };

  const openDocumentEdit = (doc: EmployeeDocument) => {
    setEditingDoc(doc);
    setDocEditName(doc.display_name || '');
    setDocEditCategory(doc.category);
    setDocEditModalOpen(true);
  };

  const openDocumentPreview = (doc: EmployeeDocument) => {
    if (!selected) return;
    setPreviewDocument({
      downloadUrl: `/api/employees/${encodeURIComponent(selected.id)}/documents/${encodeURIComponent(doc.id)}`,
      filename: doc.display_name || 'Dokument',
      contentType: doc.mime_type ?? null,
    });
  };

  const saveDocumentEdit = async () => {
    if (!selected || !editingDoc) return;
    setSavingDocEdit(true);
    try {
      const res = await fetch(`/api/employees/${encodeURIComponent(selected.id)}/documents/${encodeURIComponent(editingDoc.id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ display_name: docEditName, category: docEditCategory }),
      });
      const data = (await res.json()) as { item?: EmployeeDocument; error?: string };
      if (!res.ok || !data.item) throw new Error(data.error || 'Dokument konnte nicht bearbeitet werden.');
      setDocuments((prev) => prev.map((doc) => (doc.id === data.item!.id ? (data.item as EmployeeDocument) : doc)));
      setDocEditModalOpen(false);
      setEditingDoc(null);
    } catch (e) {
      console.error('Failed to edit employee document', e);
      setError(e instanceof Error ? e.message : 'Dokument konnte nicht bearbeitet werden.');
    } finally {
      setSavingDocEdit(false);
    }
  };

  const openEntryModal = (date: string, opts?: { fresh?: boolean; entry?: TimeEntry }) => {
    if (!selected?.start_date) {
      setError('Bitte trage zuerst ein Eintrittsdatum ein, bevor Zeiteinträge hinzugefügt werden.');
      return;
    }
    const fresh = Boolean(opts?.fresh);
    const dayEntries = timeEntries.filter((entry) => entry.entry_date === date);
    const linked = dayEntries.filter(isLinkedWorkEntry);
    // A clicked entry opens itself; a day with only one appointment/project time opens that one.
    const linkedEntry = fresh
      ? null
      : opts?.entry && isLinkedWorkEntry(opts.entry)
        ? opts.entry
        : !pickDayKeyedTimeEntry(dayEntries) && linked.length === 1
          ? linked[0]!
          : null;
    const existing = fresh ? undefined : linkedEntry ?? pickDayKeyedTimeEntry(dayEntries);
    setEntryModalLinkedEntry(linkedEntry);
    setEntryModalFresh(fresh);
    setEntryModalNotice(null);
    setEntryOverlapWarning(null);
    const holiday = holidays.find((h) => h.holiday_date === date);
    if (existing) {
      setEntryDraft({
        entry_type: existing.entry_type,
        start_time: toHm(existing.start_time),
        end_time: toHm(existing.end_time),
        break_minutes: String(normalizeBreakMinutes(existing.break_minutes)),
        note: existing.note || '',
      });
    } else if (holiday) {
      setEntryDraft({
        entry_type: 'public_holiday',
        start_time: '',
        end_time: '',
        break_minutes: '0',
        note: holiday.name,
      });
    } else {
      setEntryDraft({ entry_type: 'work', start_time: '', end_time: '', break_minutes: '0', note: '' });
    }
    setEntryModalDate(date);
  };

  /** Saves the appointment/project time open in the day dialog on that entry (its link stays). */
  const saveLinkedTimeEntry = async (linkedEntry: TimeEntry, confirmOverlap: boolean) => {
    if (!selected || !entryModalDate) return;
    const base = `/api/employees/${encodeURIComponent(selected.id)}/time-intervals/${encodeURIComponent(linkedEntry.id)}`;
    if (entryDraft.entry_type !== 'work') {
      // An absence cannot sit on a project: write it as day entry, then remove the interval.
      const put = await fetch(
        `/api/employees/${encodeURIComponent(selected.id)}/time-entries/${encodeURIComponent(entryModalDate)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            entry_type: entryDraft.entry_type,
            start_time: null,
            end_time: null,
            break_minutes: 0,
            note: entryDraft.note,
          }),
        },
      );
      const putData = (await put.json()) as { item?: TimeEntry; change_request?: unknown; error?: string };
      if (put.status === 202) {
        setEntryModalNotice('Änderungsantrag eingereicht. Die Änderung wird nach Freigabe wirksam.');
        return;
      }
      if (!put.ok || !putData.item) throw new Error(putData.error || 'Zeiteintrag konnte nicht gespeichert werden.');
      const del = await fetch(base, { method: 'DELETE', credentials: 'include' });
      if (!del.ok) {
        const delData = (await del.json().catch(() => null)) as { error?: string } | null;
        throw new Error(delData?.error || 'Projektzeit konnte nicht entfernt werden.');
      }
    } else {
      const res = await fetch(base, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employee_id: selected.id,
          target: linkedEntry.activity_id ? 'activity' : 'lead',
          activity_id: linkedEntry.activity_id ?? null,
          lead_id: linkedEntry.activity_id ? null : linkedEntry.lead_id ?? null,
          entry_date: entryModalDate,
          start_time: entryDraft.start_time || null,
          end_time: entryDraft.end_time || null,
          break_minutes: normalizeBreakMinutes(parseLocaleNumber(entryDraft.break_minutes, 0)),
          note: entryDraft.note,
          confirm_overlap: confirmOverlap,
        }),
      });
      const data = (await res.json()) as { change_request?: unknown; error?: string; code?: string };
      if (res.status === 409 && data.code === 'overlap') {
        setEntryOverlapWarning(data.error || 'Überschneidet sich mit einer anderen Arbeitszeit des Mitarbeiters.');
        return;
      }
      if (res.status === 202) {
        setEntryModalNotice('Änderungsantrag eingereicht. Die Änderung wird nach Freigabe wirksam.');
        return;
      }
      if (!res.ok) throw new Error(data.error || 'Zeiteintrag konnte nicht gespeichert werden.');
    }
    setEntryOverlapWarning(null);
    setEntryModalDate(null);
    void loadTimeData(selected.id, timeAnchor);
  };

  const saveTimeEntry = async (confirmOverlap = false) => {
    if (!selected || !entryModalDate || savingTimeEntry) return;
    setSavingTimeEntry(true);
    if (entryModalLinkedEntry) {
      try {
        await saveLinkedTimeEntry(entryModalLinkedEntry, confirmOverlap);
      } catch (e) {
        console.error('Failed to save time entry', e);
        setError(e instanceof Error ? e.message : 'Zeiteintrag konnte nicht gespeichert werden.');
      } finally {
        setSavingTimeEntry(false);
      }
      return;
    }
    try {
      const res = await fetch(`/api/employees/${encodeURIComponent(selected.id)}/time-entries/${encodeURIComponent(entryModalDate)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entry_type: entryDraft.entry_type,
          start_time: entryDraft.start_time || null,
          end_time: entryDraft.end_time || null,
          break_minutes:
            isHourTrackingEntryType(entryDraft.entry_type)
              ? normalizeBreakMinutes(parseLocaleNumber(entryDraft.break_minutes, 0))
              : 0,
          note: entryDraft.note,
          confirm_overlap: confirmOverlap,
        }),
      });
      const data = (await res.json()) as {
        item?: TimeEntry;
        code?: string;
        change_request?: {
          id: string;
          entry_date: string;
          action: 'upsert' | 'delete';
          proposed_payload?: Record<string, unknown> | null;
          previous_snapshot?: Record<string, unknown> | null;
        };
        error?: string;
      };
      if (res.status === 202 && data.change_request) {
        const { date, info } = pendingInfoFromChangeRequest(data.change_request);
        setPendingChangeRequests((prev) => {
          const next = new Map(prev);
          next.set(date, info);
          return next;
        });
        setEntryModalNotice('Änderungsantrag eingereicht. Die Änderung wird nach Freigabe wirksam.');
        return;
      }
      if (res.status === 409 && data.code === 'overlap') {
        setEntryOverlapWarning(data.error || 'Überschneidet sich mit Arbeitszeit auf einem Termin oder Projekt.');
        return;
      }
      if (!res.ok || !data.item) throw new Error(data.error || 'Zeiteintrag konnte nicht gespeichert werden.');
      setTimeEntries((prev) => withDayKeyedEntry(prev, data.item!.entry_date, data.item!));
      setEntryOverlapWarning(null);
      setEntryModalDate(null);
    } catch (e) {
      console.error('Failed to save time entry', e);
      setError(e instanceof Error ? e.message : 'Zeiteintrag konnte nicht gespeichert werden.');
    } finally {
      setSavingTimeEntry(false);
    }
  };

  const deleteTimeEntry = async () => {
    if (!selected || !entryModalDate) return;
    setDeletingTimeEntry(true);
    if (entryModalLinkedEntry) {
      try {
        const res = await fetch(
          `/api/employees/${encodeURIComponent(selected.id)}/time-intervals/${encodeURIComponent(entryModalLinkedEntry.id)}`,
          { method: 'DELETE', credentials: 'include' },
        );
        const data = (await res.json().catch(() => ({}))) as { change_request?: unknown; error?: string };
        if (res.status === 202) {
          setEntryModalNotice('Löschantrag eingereicht. Die Änderung wird nach Freigabe wirksam.');
          return;
        }
        if (!res.ok) throw new Error(data.error || 'Zeiteintrag konnte nicht gelöscht werden.');
        const removedId = entryModalLinkedEntry.id;
        setTimeEntries((prev) => prev.filter((entry) => entry.id !== removedId));
        setEntryModalDate(null);
      } catch (e) {
        console.error('Failed to delete time entry', e);
        setError(e instanceof Error ? e.message : 'Zeiteintrag konnte nicht gelöscht werden.');
      } finally {
        setDeletingTimeEntry(false);
      }
      return;
    }
    try {
      const res = await fetch(
        `/api/employees/${encodeURIComponent(selected.id)}/time-entries/${encodeURIComponent(entryModalDate)}`,
        { method: 'DELETE' }
      );
      const data = (await res.json()) as {
        ok?: boolean;
        change_request?: {
          id: string;
          entry_date: string;
          action: 'upsert' | 'delete';
          proposed_payload?: Record<string, unknown> | null;
          previous_snapshot?: Record<string, unknown> | null;
        };
        error?: string;
      };
      if (res.status === 202 && data.change_request) {
        const { date, info } = pendingInfoFromChangeRequest(data.change_request);
        setPendingChangeRequests((prev) => {
          const next = new Map(prev);
          next.set(date, info);
          return next;
        });
        setEntryModalNotice('Löschantrag eingereicht. Die Änderung wird nach Freigabe wirksam.');
        return;
      }
      if (!res.ok || !data.ok) throw new Error(data.error || 'Zeiteintrag konnte nicht gelöscht werden.');
      setTimeEntries((prev) => withDayKeyedEntry(prev, entryModalDate, null));
      setEntryModalDate(null);
    } catch (e) {
      console.error('Failed to delete time entry', e);
      setError(e instanceof Error ? e.message : 'Zeiteintrag konnte nicht gelöscht werden.');
    } finally {
      setDeletingTimeEntry(false);
    }
  };

  const renderedDocuments = React.useMemo(() => {
    if (docFilter === 'all') return documents;
    return documents.filter((doc) => doc.category === docFilter);
  }, [documents, docFilter]);

  const documentFilterOptions = React.useMemo(
    () => employeeDocumentFilterOptions(documentTypes),
    [documentTypes],
  );
  const enabledDocumentTypes = React.useMemo(
    () => enabledEmployeeDocumentTypes(documentTypes),
    [documentTypes],
  );
  const uploadCategoryOptions = enabledDocumentTypes;
  const editCategoryOptions = React.useMemo(() => {
    if (!docEditCategory || enabledDocumentTypes.some((row) => row.key === docEditCategory)) {
      return enabledDocumentTypes;
    }
    const current =
      documentTypes.find((row) => row.key === docEditCategory) ?? {
        key: docEditCategory,
        label: employeeDocumentTypeLabel(documentTypes, docEditCategory),
        sort_order: enabledDocumentTypes.length,
        is_enabled: false,
        is_system: false,
      };
    return [...enabledDocumentTypes, current];
  }, [documentTypes, enabledDocumentTypes, docEditCategory]);

  React.useEffect(() => {
    const enabledKeys = new Set(enabledDocumentTypes.map((row) => row.key));
    const firstEnabled = enabledDocumentTypes[0]?.key ?? EMPLOYEE_DOCUMENT_TYPE_OTHER_KEY;
    setPendingCategory((prev) => (enabledKeys.has(prev) ? prev : firstEnabled));
    setDocFilter((prev) => (prev === 'all' || enabledKeys.has(prev) ? prev : 'all'));
  }, [enabledDocumentTypes]);

  const monthCells = React.useMemo(() => buildMonthGrid(timeAnchor), [timeAnchor]);
  const monthName = timeAnchor.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
  const monthRange = React.useMemo(() => monthBounds(timeAnchor), [timeAnchor]);
  const startDate = selected?.start_date ?? null;
  const isHolidayCreditDateForSelected = React.useCallback(
    (date: string) => isHolidayCreditDate(date, startDate),
    [startDate]
  );

  const entryByDate = React.useMemo(() => {
    const map = new Map<string, TimeEntry>();
    timeEntries.forEach((entry) => map.set(entry.entry_date, entry));
    return map;
  }, [timeEntries]);
  const holidayByDate = React.useMemo(() => {
    const map = new Map<string, Holiday>();
    holidays.forEach((holiday) => map.set(holiday.holiday_date, holiday));
    return map;
  }, [holidays]);

  const monthEntries = React.useMemo(() => {
    const from = toDateString(monthRange.from);
    const to = toDateString(monthRange.to);
    return timeEntries.filter((entry) => entry.entry_date >= from && entry.entry_date <= to);
  }, [timeEntries, monthRange]);

  const autoHolidayEntries = React.useMemo(() => {
    const from = toDateString(monthRange.from);
    const to = toDateString(monthRange.to);
    const existingDates = new Set(monthEntries.map((entry) => entry.entry_date));

    return holidays
      .filter((holiday) => {
        if (holiday.holiday_date < from || holiday.holiday_date > to) return false;
        if (existingDates.has(holiday.holiday_date)) return false;
        return true;
      })
      .map<TimeEntry>((holiday) => ({
        id: `holiday-${holiday.holiday_date}`,
        employee_id: selected?.id ?? '',
        entry_date: holiday.holiday_date,
        entry_type: 'public_holiday',
        start_time: null,
        end_time: null,
        break_minutes: 0,
        note: holiday.name || '',
        created_at: '',
        updated_at: '',
      }));
  }, [holidays, monthRange, monthEntries, selected?.id]);

  const displayedMonthEntries = React.useMemo(() => {
    return [...monthEntries, ...autoHolidayEntries].sort((a, b) => a.entry_date.localeCompare(b.entry_date));
  }, [monthEntries, autoHolidayEntries]);

  const displayedEntryByDate = React.useMemo(() => {
    const grouped = new Map<string, TimeEntry[]>();
    displayedMonthEntries.forEach((entry) => {
      const list = grouped.get(entry.entry_date) ?? [];
      list.push(entry);
      grouped.set(entry.entry_date, list);
    });
    const map = new Map<string, TimeEntry>();
    grouped.forEach((list, date) => {
      const picked = pickCalendarDayEntry(list);
      if (picked) map.set(date, picked);
    });
    return map;
  }, [displayedMonthEntries]);

  const metrics = React.useMemo(() => {
    const expectedPerDay = selected?.expected_hours_per_day ?? 8;
    const workEntries = monthEntries.filter((e) => e.entry_type === 'work');
    const workHours = workEntries.reduce(
      (acc, row) => acc + calcWorkHours(row.start_time, row.end_time, row.break_minutes),
      0
    );
    const sicknessDays = displayedMonthEntries.filter((e) => e.entry_type === 'sickness').length;
    const year = timeAnchor.getFullYear();
    const todayYmd = toDateString(new Date());
    const { from: yearFrom, to: yearTo } = yearBounds(year);
    const { from: monthFrom, to: monthTo } = monthRange;
    const monthFromStr = toDateString(monthFrom);
    const monthToStr = toDateString(monthTo);
    const startDate = selected?.start_date ?? null;
    const endDate = selected?.end_date ?? null;
    const {
      entitlement,
      fullYear,
      statutoryFull,
      additionalFull,
      isProrated,
      hasPartialStartMonth,
    } = proratedAnnualVacationEntitlement(
      selected?.statutory_vacation_days ?? 0,
      selected?.additional_vacation_days ?? 0,
      startDate,
      endDate,
      year
    );
    const takenYear = countVacationWeekdays(timeEntries, yearFrom, yearTo, todayYmd, startDate, endDate, 'taken');
    const plannedYear = countVacationWeekdays(timeEntries, yearFrom, yearTo, todayYmd, startDate, endDate, 'planned');
    const takenMonth = countVacationWeekdays(timeEntries, monthFromStr, monthToStr, todayYmd, startDate, endDate, 'taken');
    const plannedMonth = countVacationWeekdays(timeEntries, monthFromStr, monthToStr, todayYmd, startDate, endDate, 'planned');
    const remaining = entitlement - takenYear - plannedYear;
    const vacationBalance = {
      year,
      entitlement,
      fullYear,
      statutoryFull,
      additionalFull,
      isProrated,
      hasPartialStartMonth,
      remaining,
      takenYear,
      plannedYear,
      takenMonth,
      plannedMonth,
    };
    const holidayWeekdays = displayedMonthEntries.filter((e) => e.entry_type === 'public_holiday' && isHolidayCreditDateForSelected(e.entry_date)).length;
    const holidayCreditHours = holidayWeekdays * expectedPerDay;
    // Freischicht reduziert das Soll (geplanter freier Tag, keine erwartete Arbeit).
    // Freizeitausgleich (AZK) reduziert das Soll NICHT — die geleisteten AZK-Stunden werden
    // direkt vom Überstundenkonto abgebucht, damit n AZK-Stunden die Überstunden um exakt n senken.
    const shiftFreeWeekdays = monthEntries.filter(
      (e) => e.entry_type === 'shift_free' && isWeekdayYmd(e.entry_date)
    ).length;
    const azkHours = monthEntries
      .filter((e) => e.entry_type === 'overtime_comp' && isWeekdayYmd(e.entry_date))
      .length * expectedPerDay;
    const totalWeekdays = monthCells.filter(
      (d) => d.getMonth() === timeAnchor.getMonth() && d.getDay() !== 0 && d.getDay() !== 6
    ).length;
    const baselineWeekdays = Math.max(0, totalWeekdays - shiftFreeWeekdays);
    const overtime = workHours + holidayCreditHours - baselineWeekdays * expectedPerDay - azkHours;
    return { workHours: workHours + holidayCreditHours, overtime, sicknessDays, vacationBalance };
  }, [
    monthEntries,
    displayedMonthEntries,
    timeEntries,
    monthRange,
    selected?.expected_hours_per_day,
    selected?.statutory_vacation_days,
    selected?.additional_vacation_days,
    selected?.start_date,
    selected?.end_date,
    monthCells,
    timeAnchor,
    isHolidayCreditDateForSelected,
  ]);

  React.useEffect(() => {
    if (activeTab !== 'time-tracking') return;
    const fromUrl = parseMonthQueryParam(searchParams.get('month'));
    if (!fromUrl) return;
    setTimeAnchor((prev) => {
      if (prev.getFullYear() === fromUrl.getFullYear() && prev.getMonth() === fromUrl.getMonth()) {
        return prev;
      }
      return fromUrl;
    });
  }, [activeTab, searchParams]);

  React.useEffect(() => {
    if (!selectedId || activeTab !== 'time-tracking') return;
    const month = monthQueryParamFromDate(timeAnchor);
    if (searchParams.get('month') === month) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('month', month);
        return next;
      },
      { replace: true },
    );
  }, [timeAnchor, activeTab, selectedId, searchParams, setSearchParams]);

  const dailyList = React.useMemo(() => {
    const from = toDateString(monthRange.from);
    const to = toDateString(monthRange.to);
    const base = displayedMonthEntries.filter((entry) => {
      if (entry.entry_date < from || entry.entry_date > to) return false;
      if (entry.entry_type !== 'public_holiday') return true;
      return isHolidayCreditDateForSelected(entry.entry_date);
    });
    const datesInList = new Set(base.map((entry) => normalizeChangeRequestDate(entry.entry_date)));
    const extras: TimeEntry[] = [];
    for (const [date, info] of pendingChangeRequests) {
      if (date < from || date > to || datesInList.has(date)) continue;
      if (info.action === 'delete' || !info.entry_type) continue;
      extras.push({
        id: `pending-${date}`,
        employee_id: selected?.id ?? '',
        entry_date: date,
        entry_type: info.entry_type,
        start_time: info.start_time,
        end_time: info.end_time,
        break_minutes: info.break_minutes,
        note: '',
        created_at: '',
        updated_at: '',
      });
    }
    return [...base, ...extras].sort((a, b) => a.entry_date.localeCompare(b.entry_date));
  }, [
    displayedMonthEntries,
    isHolidayCreditDateForSelected,
    monthRange,
    pendingChangeRequests,
    selected?.id,
  ]);

  const canDeleteEntry = Boolean(entryModalLinkedEntry) || canDeletePersistedTimeEntry(entryModalDate, timeEntries);
  const canEditIntervals = can('web.hr.edit');
  // Other entries of the day, listed when the dialog edits the day entry (not for „+ Eintrag“).
  const entryModalLinked = entryModalDate && !entryModalFresh
    ? timeEntries.filter(
        (entry) =>
          entry.entry_date === entryModalDate && isLinkedWorkEntry(entry) && entry.id !== entryModalLinkedEntry?.id,
      )
    : [];
  const isDisplayedMonthClosed = React.useMemo(() => {
    const ym = payrollYearMonthKey(timeAnchor.getFullYear(), timeAnchor.getMonth() + 1);
    return payrollPeriodStatusMap.get(ym) === 'closed';
  }, [timeAnchor, payrollPeriodStatusMap]);
  const hasPendingChangeRequest = React.useCallback(
    (dateKey: string) => hasPendingChangeRequestForDate(pendingChangeRequests, dateKey),
    [pendingChangeRequests],
  );
  const entryModalInClosedMonth = entryModalDate
    ? isClosedPayrollMonth(entryModalDate, payrollPeriodStatusMap)
    : false;

  return (
    <>
      <div className="h-screen flex bg-app-surface font-sans overflow-hidden">
      <AppSidebar active="employees" />
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden pb-16 md:pb-0">
        <AppTopBar title="Mitarbeiterverwaltung" />
        <section className="flex-1 min-h-0">
          <div className="h-full bg-app-bg overflow-hidden flex border-t border-app-border">
            <EmployeeListRail
              employees={employees}
              loading={loading}
              search={search}
              onSearchChange={setSearch}
              workAreaFilter={workAreaFilter}
              onWorkAreaFilterChange={handleWorkAreaFilterChange}
              canAccessInternal={canAccessInternal}
              overviewActive={isOverviewRoute}
              selectedEmployeeId={selectedId}
              activeEmployees={activeEmployees}
              inactiveEmployees={inactiveEmployees}
              inactiveOpen={inactiveOpen}
              onInactiveOpenChange={setInactiveOpen}
              onOpenOverview={() => navigateToOverview('uebersicht')}
              onSelectEmployee={(id) => navigateToEmployee(id, 'profile')}
              onNewEmployee={() => setShowWizard(true)}
              getInitials={getInitials}
              formatLastSeenLabel={formatLastSeenLabel}
              avatarClass={(colorIdx) => AVATAR_COLORS[colorIdx % AVATAR_COLORS.length]}
            />

            <div className="flex-1 min-w-0 overflow-hidden flex flex-col bg-app-bg">
              {isOverviewRoute && instanceId ? (
                <EmployeeOverviewShell
                  instanceId={instanceId}
                  activeTab={overviewTab}
                  periodQuery={searchParams.toString()}
                >
                  {overviewTab === 'uebersicht' && (
                    <EmployeeOverviewDashboardTab
                      from={overviewPeriod.from}
                      to={overviewPeriod.to}
                      granularity={overviewPeriod.granularity}
                      onGranularityChange={overviewPeriod.setGranularity}
                      onShift={overviewPeriod.shift}
                      onApplyCustomPeriod={overviewPeriod.applyCustomPeriod}
                      onRowClick={(id) => navigateToEmployee(id, 'time-tracking')}
                      onOpenRequestsTab={() => navigateToOverview('antraege')}
                    />
                  )}
                  {overviewTab === 'antraege' && <EmployeeOverviewRequestsTab />}
                  {overviewTab === 'be-abzuege' && (
                    <EmployeeBeAbzuegeTab
                      from={overviewPeriod.from}
                      to={overviewPeriod.to}
                      onShift={overviewPeriod.shift}
                      onApplyCustomPeriod={overviewPeriod.applyCustomPeriod}
                    />
                  )}
                  {overviewTab === 'lohnbuchhaltung' && (
                    <EmployeePayrollTab
                      from={overviewPeriod.from}
                      to={overviewPeriod.to}
                      onShift={overviewPeriod.shift}
                      onApplyCustomPeriod={overviewPeriod.applyCustomPeriod}
                      onOpenEmployeeTimeTracking={(id) => navigateToEmployee(id, 'time-tracking')}
                      onTimeEntrySaved={() => void loadEmployees()}
                    />
                  )}
                </EmployeeOverviewShell>
              ) : !selected ? (
                <div className="flex-1 grid place-items-center text-app-text-subtle text-sm">Mitarbeiter auswählen</div>
              ) : (
                <>
                  <div className="app-scrollbar flex-1 min-h-0 overflow-y-auto overscroll-contain">
                    <div className="max-w-[1100px] mx-auto px-6 pt-5 pb-8">

                      <div className="flex items-end justify-between gap-3 border-b-2 border-app-border mb-5">
                        <div className="flex items-end gap-0 min-w-0">
                          {([
                            ['profile', 'Stammdaten', <User className="w-[14px] h-[14px]" />],
                            ['documents', 'Dokumente', <FileText className="w-[14px] h-[14px]" />],
                            ['be-abzuege', 'Be- und Abzüge', <Scale className="w-[14px] h-[14px]" />],
                            ['time-tracking', 'Zeiterfassung', <Clock className="w-[14px] h-[14px]" />],
                          ] as Array<[TabId, string, React.ReactNode]>).map(([id, label, icon]) => (
                            <button
                              key={id}
                              type="button"
                              onClick={() => selected && navigateToEmployee(selected.id, id)}
                              className={`inline-flex items-center gap-1.5 px-[18px] py-[10px] text-sm font-medium border-b-2 -mb-0.5 transition-colors ${
                                activeTab === id
                                  ? 'text-app-text border-blue-600'
                                  : 'text-app-text-subtle border-transparent hover:text-app-text-muted'
                              }`}
                            >
                              <span className={activeTab === id ? 'text-blue-600' : 'text-app-text-subtle'}>{icon}</span>
                              {label}
                            </button>
                          ))}
                        </div>
                        {selected.user_id ? (
                          <span className="text-xs font-medium text-emerald-600 shrink-0 pb-2.5">
                            Eingeladen
                          </span>
                        ) : canInviteUser ? (
                          <button
                            type="button"
                            disabled={inviting}
                            onClick={openInviteModal}
                            className="inline-flex items-center gap-1.5 rounded-md border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 disabled:opacity-60 shrink-0 mb-1.5"
                          >
                            <UserRoundPlus className="w-3.5 h-3.5" />
                            Zugang einladen
                          </button>
                        ) : null}
                      </div>

                    {activeTab === 'profile' && profileDraft && (
                      <div className="space-y-4">
                        <div className="bg-app-surface border border-app-border rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.07)] px-5 py-[20px] flex items-center gap-4">
                          <span
                            className={`w-16 h-16 rounded-full grid place-items-center text-xl font-bold shrink-0 ${
                              AVATAR_COLORS[selected.color_idx % AVATAR_COLORS.length].bg
                            } ${AVATAR_COLORS[selected.color_idx % AVATAR_COLORS.length].text}`}
                          >
                            {getInitials(selected)}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-lg font-semibold text-app-text leading-snug truncate">
                              {[selected.first_name, selected.last_name].filter(Boolean).join(' ') || 'Unbenannter Mitarbeiter'}
                            </p>
                            <p className="text-sm text-app-text-muted mt-0.5 truncate">
                              {[selected.position, selected.department].filter(Boolean).join(' · ') || '-'}
                            </p>
                            {selected.start_date && (
                              <p className="text-xs text-app-text-subtle mt-1">Seit {formatDate(selected.start_date)}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {editMode ? (
                              <>
                                <button
                                  type="button"
                                  onClick={() => void toggleArchive(!selected.is_archived)}
                                  disabled={savingProfile}
                                  className="inline-flex h-9 items-center gap-1.5 rounded-md border border-app-border bg-app-surface px-3.5 text-sm text-app-text-muted hover:bg-app-hover disabled:opacity-60"
                                >
                                  <Clock className="w-3.5 h-3.5" />
                                  {selected.is_archived ? 'Reaktivieren' : 'Archivieren'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setPendingDeleteEmployee(selected)}
                                  disabled={savingProfile || Boolean(deletingEmployeeId)}
                                  className="app-btn-danger-outline inline-flex h-9 items-center gap-1.5 rounded-md border border-red-200 bg-app-surface px-3.5 text-sm text-red-600 hover:bg-red-50 disabled:opacity-60"
                                >
                                  <Trash2 className="w-3.5 h-3.5" /> Löschen
                                </button>
                                <button
                                  type="button"
                                  onClick={() => { setEditMode(false); setProfileDraft(selected); setProfileSaveAttempted(false); }}
                                  className="inline-flex h-9 items-center gap-1.5 rounded-md border border-app-border bg-app-surface px-3.5 text-sm text-app-text-muted hover:bg-app-hover"
                                >
                                  <X className="w-3.5 h-3.5" /> Abbrechen
                                </button>
                                <button
                                  type="button"
                                  onClick={() => void saveDraft()}
                                  disabled={savingProfile}
                                  className="inline-flex h-9 items-center gap-1.5 rounded-md border-none bg-blue-600 px-3.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                                >
                                  <Check className="w-3.5 h-3.5" /> {savingProfile ? 'Speichert…' : 'Speichern'}
                                </button>
                              </>
                            ) : (
                              <button
                                type="button"
                                onClick={() => { setEditMode(true); setProfileSaveAttempted(false); }}
                                className="inline-flex h-9 items-center gap-1.5 rounded-md border border-app-border bg-app-surface px-3.5 text-sm text-app-text-muted hover:bg-app-hover"
                              >
                                <Edit className="w-3.5 h-3.5" /> Bearbeiten
                              </button>
                            )}
                          </div>
                        </div>
                        {EMPLOYEE_STAMMDATEN_EXPORT_GROUPS.map((group) => {
                          let fields = toStammdatenSectionFields(group);
                          if (group.id === 'compensation') {
                            const type =
                              profileDraft.compensation_type === 'salary' ? 'salary' : 'hourly';
                            fields = fields.filter((field) => {
                              if (field.key === 'compensation_type') return false;
                              if (type === 'salary') {
                                return field.key !== 'hourly_wage' && field.key !== 'allowance_per_hour';
                              }
                              return field.key !== 'monthly_salary';
                            });
                          }
                          return (
                            <StammdatenSection
                              key={group.id}
                              title={group.title}
                              icon={STAMMDATEN_SECTION_ICONS[group.id]}
                              fields={fields}
                              draft={profileDraft}
                              editMode={editMode}
                              showValidationErrors={profileSaveAttempted}
                              headerExtra={
                                group.id === 'compensation' ? (
                                  <CompensationTypeSwitch
                                    value={
                                      profileDraft.compensation_type === 'salary' ? 'salary' : 'hourly'
                                    }
                                    editMode={editMode}
                                    onChange={(value) =>
                                      setProfileDraft((prev) =>
                                        prev ? { ...prev, compensation_type: value } : prev,
                                      )
                                    }
                                  />
                                ) : undefined
                              }
                              onPatch={(key, value) =>
                                setProfileDraft((prev) =>
                                  prev ? ({ ...prev, [key]: value } as Employee) : prev,
                                )
                              }
                              canAccessInternal={canAccessInternal}
                            />
                          );
                        })}
                        {!editMode && (
                          <EmployeeMasterDataHistory employeeId={selected.id} refreshKey={masterDataHistoryKey} />
                        )}
                      </div>
                    )}

                    {activeTab === 'documents' && (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            {documentFilterOptions.map((filter) => (
                              <button
                                key={filter.id}
                                type="button"
                                onClick={() => setDocFilter(filter.id)}
                                className={`px-2.5 py-1 rounded-md text-xs border ${
                                  docFilter === filter.id
                                    ? 'bg-blue-50 border-blue-300 text-blue-700'
                                    : 'bg-app-surface border-app-border text-app-text-muted'
                                }`}
                              >
                                {filter.label}
                              </button>
                            ))}
                          </div>
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="inline-flex items-center gap-1.5 rounded-md bg-pipedrive-blue px-3 py-1.5 text-xs font-semibold text-white"
                          >
                            <Upload className="w-3.5 h-3.5" />
                            Dokument hochladen
                          </button>
                        </div>

                        <div className="rounded-xl bg-app-surface border border-app-border shadow-sm overflow-hidden">
                          <table className="w-full">
                            <thead>
                              <tr className="bg-app-surface-muted border-b border-app-border">
                                <th className="px-3 py-2.5 text-left text-xs font-medium text-app-text-muted">Dateiname</th>
                                <th className="px-3 py-2.5 text-left text-xs font-medium text-app-text-muted">Typ</th>
                                <th className="px-3 py-2.5 text-left text-xs font-medium text-app-text-muted">Größe</th>
                                <th className="px-3 py-2.5 text-left text-xs font-medium text-app-text-muted">Hochgeladen</th>
                                <th className="px-3 py-2.5 text-right text-xs font-medium text-app-text-muted">Aktionen</th>
                              </tr>
                            </thead>
                            <tbody>
                              {documentsLoading && (
                                <tr>
                                  <td colSpan={5} className="px-3 py-4">
                                    <InlineLoadingState label="Lade Dokumente…" className="text-xs" />
                                  </td>
                                </tr>
                              )}
                              {!documentsLoading && renderedDocuments.length === 0 && (
                                <tr>
                                  <td colSpan={5} className="px-3 py-4 text-center text-xs text-app-text-subtle">Keine Dokumente.</td>
                                </tr>
                              )}
                              {!documentsLoading &&
                                renderedDocuments.map((doc) => (
                                  <tr key={doc.id} className="border-b border-app-border">
                                    <td className="px-3 py-3 text-sm text-app-text">
                                      <div className="flex items-center gap-2">
                                        <FileText className="w-4 h-4 text-app-text-subtle" />
                                        <button
                                          type="button"
                                          onClick={() => openDocumentPreview(doc)}
                                          className="truncate text-left text-app-text hover:text-blue-600 hover:underline"
                                          title="Datei anzeigen"
                                        >
                                          {doc.display_name}
                                        </button>
                                      </div>
                                    </td>
                                    <td className="px-3 py-3 text-xs text-app-text-muted">{employeeDocumentTypeLabel(documentTypes, doc.category)}</td>
                                    <td className="px-3 py-3 text-xs text-app-text-muted">{formatBytes(doc.size_bytes)}</td>
                                    <td className="px-3 py-3 text-xs text-app-text-muted">{formatTimestamp(doc.created_at)}</td>
                                    <td className="px-3 py-3">
                                      <div className="flex justify-end items-center gap-1">
                                        <button
                                          type="button"
                                          onClick={() => openDocumentEdit(doc)}
                                          className="p-1 rounded hover:bg-app-hover"
                                          title="Dokument bearbeiten"
                                          aria-label="Dokument bearbeiten"
                                        >
                                          <Pencil className="w-4 h-4 text-app-text-subtle" />
                                        </button>
                                        <a
                                          href={`/api/employees/${encodeURIComponent(selected.id)}/documents/${encodeURIComponent(doc.id)}?download=1`}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="p-1 rounded hover:bg-sky-50"
                                          title="Dokument herunterladen"
                                          aria-label="Dokument herunterladen"
                                        >
                                          <Download className="w-4 h-4 text-blue-600" />
                                        </a>
                                        <button
                                          type="button"
                                          onClick={() => void deleteDocument(doc)}
                                          disabled={deletingDocId === doc.id}
                                          className="p-1 rounded hover:bg-red-50 disabled:opacity-50"
                                          title="Dokument löschen"
                                          aria-label="Dokument löschen"
                                        >
                                          <Trash2 className="w-4 h-4 text-red-500" />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>

                        <button
                          type="button"
                          onDrop={handleDocDrop}
                          onDragOver={handleDocDragOver}
                          onDragLeave={handleDocDragLeave}
                          onClick={() => dropFileInputRef.current?.click()}
                          className={`w-full rounded-lg border border-dashed px-4 py-7 text-center text-sm transition-colors cursor-pointer select-none ${
                            isDocDragging
                              ? 'border-blue-400 bg-blue-50 text-blue-700'
                              : 'border-app-border-strong bg-app-surface-muted/70 text-app-text-subtle hover:bg-app-hover'
                          }`}
                        >
                          Datei hier ablegen oder <span className="text-blue-600 font-medium">auswählen</span>
                          <div className="text-xs text-app-text-subtle mt-1">PDF, JPG, PNG bis 25 MB</div>
                        </button>
                      </div>
                    )}

                    {activeTab === 'be-abzuege' && selected && (
                      <EmployeeProfileBeAbzuegeTab
                        employeeId={selected.id}
                        employeeName={
                          [selected.first_name, selected.last_name].filter(Boolean).join(' ').trim() ||
                          'Unbenannter Mitarbeiter'
                        }
                      />
                    )}

                    {activeTab === 'time-tracking' && (
                      <div className="space-y-4">
                        {!selected.start_date && (
                          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                            Bitte trage zuerst ein Eintrittsdatum in den Stammdaten ein, bevor Zeiteinträge hinzugefügt werden können.
                          </div>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                          <MetricCard icon={<Clock3 className="w-4 h-4 text-blue-600" />} label="Arbeitsstunden" value={`${metrics.workHours.toFixed(2)} h`} />
                          <MetricCard icon={<Calendar className="w-4 h-4 text-blue-600" />} label="Überstunden" value={`${metrics.overtime >= 0 ? '+' : ''}${metrics.overtime.toFixed(2)} h`} />
                          <MetricCard
                            icon={<Calendar className="w-4 h-4 text-amber-600" />}
                            label={`Urlaub ${metrics.vacationBalance.year} (verbleibend / Anspruch)`}
                            value={`${formatVacationDayCount(metrics.vacationBalance.remaining)} / ${formatVacationDayCount(metrics.vacationBalance.entitlement)}`}
                            infoContent={
                              <VacationBalanceInfo balance={metrics.vacationBalance} />
                            }
                          />
                          <MetricCard icon={<HeartPulse className="w-4 h-4 text-rose-600" />} label="Krankheitstage" value={`${metrics.sicknessDays} Tage`} />
                        </div>

                        <div className="grid grid-cols-1 xl:grid-cols-[2fr_1fr] gap-4">
                          <div className="space-y-2">
                            <div ref={calendarCardRef} className="rounded-xl bg-app-surface border border-app-border shadow-sm">
                            <div className="px-4 py-3 flex items-center justify-between gap-2">
                              <button
                                type="button"
                                onClick={() => setTimeAnchor((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1))}
                                className="w-7 h-7 rounded-md border border-app-border grid place-items-center text-app-text-muted"
                              >
                                <ChevronLeft className="w-4 h-4" />
                              </button>
                              <div className="flex flex-col items-center gap-1 min-w-0">
                                <p className="text-sm font-semibold text-app-text capitalize">{monthName}</p>
                                {isDisplayedMonthClosed ? <PayrollPeriodClosedBadge /> : null}
                              </div>
                              <button
                                type="button"
                                onClick={() => setTimeAnchor((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1))}
                                className="w-7 h-7 rounded-md border border-app-border grid place-items-center text-app-text-muted"
                              >
                                <ChevronRight className="w-4 h-4" />
                              </button>
                            </div>
                            <div className="grid grid-cols-[2.25rem_repeat(7,minmax(0,1fr))] text-xs font-medium text-app-text-muted px-3 pt-3 pb-2">
                              <div className="text-center">KW</div>
                              {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((d) => (
                                <div key={d} className="text-center">{d}</div>
                              ))}
                            </div>
                            <div className="grid grid-cols-[2.25rem_repeat(7,minmax(0,1fr))] gap-1 px-3 pb-3">
                              {Array.from({ length: 6 }, (_, weekIdx) => {
                                const weekDays = monthCells.slice(weekIdx * 7, weekIdx * 7 + 7);
                                const weekRef = weekDays.find((d) => d.getMonth() === timeAnchor.getMonth()) ?? weekDays[0];
                                const kw = getIsoWeek(weekRef);
                                return (
                                  <React.Fragment key={`week-${weekIdx}`}>
                                    <div className="flex items-center justify-center text-xs font-medium text-app-text-muted">
                                      {kw}
                                    </div>
                                    {weekDays.map((d) => {
                                      const dateKey = toDateString(d);
                                      const entry = displayedEntryByDate.get(dateKey);
                                      const inMonth = d.getMonth() === timeAnchor.getMonth();
                                      const weekend = d.getDay() === 0 || d.getDay() === 6;
                                      const type = effectiveEntryTypeForPendingDay(
                                        dateKey,
                                        entry?.entry_type ?? null,
                                        pendingChangeRequests,
                                      );
                                      const dayInClosedMonth = isClosedPayrollMonth(dateKey, payrollPeriodStatusMap);
                                      const dayChangePending = hasPendingChangeRequest(dateKey);
                                      const expectedPerDay = selected?.expected_hours_per_day ?? 8;
                                      const bgClass =
                                        !inMonth ? 'bg-app-surface-muted text-app-text-subtle' :
                                        type ? ENTRY_TYPE_COLORS[type] :
                                        weekend ? 'bg-app-surface-muted text-app-text-subtle' : 'bg-app-surface text-app-text-muted border border-app-border';
                                      return (
                                        <button
                                          key={dateKey}
                                          type="button"
                                          onClick={() => openEntryModal(dateKey)}
                                          disabled={!selected.start_date}
                                          title={
                                            !selected.start_date
                                              ? 'Bitte zuerst ein Eintrittsdatum eintragen.'
                                              : dayChangePending
                                                ? 'Änderung beantragt'
                                                : dayInClosedMonth
                                                  ? 'Abrechnungsmonat abgeschlossen'
                                                  : undefined
                                          }
                                          className={`relative h-16 rounded-md px-2 py-1 text-left text-xs overflow-hidden ${bgClass} ${!selected.start_date ? 'cursor-not-allowed' : ''} ${dayInClosedMonth ? 'ring-1 ring-inset ring-app-border-strong' : ''}`}
                                        >
                                          {dayChangePending ? (
                                            <span
                                              className="pointer-events-none absolute inset-0 rounded-md bg-[repeating-linear-gradient(-45deg,transparent,transparent_4px,rgba(0,0,0,0.06)_4px,rgba(0,0,0,0.06)_8px)]"
                                              aria-hidden
                                            />
                                          ) : null}
                                          {dayInClosedMonth ? (
                                            <Lock className="absolute bottom-1 right-1 z-[1] h-2.5 w-2.5 text-app-text-subtle/90" aria-hidden />
                                          ) : null}
                                          {entry?.note?.trim() ? (
                                            <MessageSquareText className="absolute right-1.5 top-1.5 z-[1] h-3 w-3 text-app-text-subtle" />
                                          ) : null}
                                          <div className="relative z-[1] font-semibold">{d.getDate()}</div>
                                          <div className="relative z-[1] text-xs mt-1 opacity-85">
                                            {entry?.entry_type === 'work'
                                              ? `${formatDisplayHours(sumWorkHoursOnDate(displayedMonthEntries, dateKey))} h`
                                              : isFixedDayHoursEntryType(entry?.entry_type ?? '') && isWeekdayYmd(dateKey)
                                                ? `${formatDisplayHours(expectedPerDay)} h`
                                              : entry?.entry_type === 'public_holiday' && isHolidayCreditDateForSelected(dateKey)
                                                ? `${formatDisplayHours(expectedPerDay)} h`
                                              : ''}
                                          </div>
                                        </button>
                                      );
                                    })}
                                  </React.Fragment>
                                );
                              })}
                            </div>
                            <div className="px-3 pb-3 flex flex-wrap gap-2 text-xs text-app-text-subtle">
                              {ENTRY_TYPE_ORDER.map((type) => (
                                <span key={type} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${ENTRY_TYPE_COLORS[type]}`}>
                                  {ENTRY_TYPE_LABEL[type]}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>

                        <div
                          className="rounded-xl bg-app-surface border border-app-border shadow-sm min-h-0 flex flex-col xl:h-[var(--calendar-card-height)]"
                          style={
                            calendarCardHeight
                              ? ({ '--calendar-card-height': `${calendarCardHeight}px` } as React.CSSProperties)
                              : undefined
                          }
                        >
                            <div className="px-4 pt-3 pb-1 flex items-center justify-between shrink-0 gap-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <p className="text-sm font-semibold text-app-text">Tageseinträge</p>
                                {isDisplayedMonthClosed ? <PayrollPeriodClosedBadge compact /> : null}
                              </div>
                              <button
                                type="button"
                                onClick={() => openEntryModal(toDateString(new Date()), { fresh: true })}
                                disabled={!selected.start_date}
                                title={!selected.start_date ? 'Bitte zuerst ein Eintrittsdatum eintragen.' : undefined}
                                className="inline-flex items-center gap-1 rounded-md bg-pipedrive-blue px-2 py-1 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:bg-app-surface-muted disabled:text-app-text-subtle"
                              >
                                <Plus className="w-3.5 h-3.5" />
                                Eintrag
                              </button>
                            </div>
                            <div className="app-scrollbar flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 py-2">
                              {timeLoading && (
                                <InlineLoadingState label="Lade Einträge…" className="justify-start py-3 text-xs" />
                              )}
                              {!timeLoading && dailyList.length === 0 && <p className="text-xs text-app-text-subtle py-3">Keine Einträge.</p>}
                              {!timeLoading &&
                                dailyList.map((entry) => {
                                  const entryClosed = isClosedPayrollMonth(entry.entry_date, payrollPeriodStatusMap);
                                  const entryChangePending = hasPendingChangeRequest(entry.entry_date);
                                  const displayEntry = resolvePendingListEntryFields(
                                    {
                                      entry_type: entry.entry_type,
                                      start_time: entry.start_time,
                                      end_time: entry.end_time,
                                      break_minutes: normalizeBreakMinutes(entry.break_minutes),
                                    },
                                    entry.entry_date,
                                    pendingChangeRequests,
                                  );
                                  const displayType = displayEntry.entry_type;
                                  return (
                                  <button
                                    key={entry.id}
                                    type="button"
                                    onClick={() => openEntryModal(entry.entry_date, { entry })}
                                    className="w-full text-left flex items-center gap-2 rounded-md px-1 py-2 hover:bg-app-hover"
                                  >
                                    <span className="w-6 shrink-0 text-xs tabular-nums text-app-text-subtle">{Number(entry.entry_date.slice(8, 10))}.</span>
                                    <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${ENTRY_TYPE_COLORS[displayType]}`}>
                                      {ENTRY_TYPE_LABEL[displayType]}
                                    </span>
                                    <span className="text-xs text-app-text-muted flex-1 min-w-0 truncate">
                                      {isHourTrackingEntryType(displayType) ? (
                                        <>
                                          {toHm(displayEntry.start_time)} – {toHm(displayEntry.end_time)}
                                          {normalizeBreakMinutes(displayEntry.break_minutes) > 0
                                            ? ` · ${normalizeBreakMinutes(displayEntry.break_minutes)} Min Pause`
                                            : ''}
                                          {entryChangePending ? ' (beantragt)' : ''}
                                        </>
                                      ) : (
                                        <>
                                          Ganztägig{entryChangePending ? ' (beantragt)' : ''}
                                        </>
                                      )}
                                    </span>
                                    <span className="text-xs text-app-text-subtle shrink-0">
                                      {isHourTrackingEntryType(displayType)
                                        ? `${formatDisplayHours(calcWorkHours(displayEntry.start_time, displayEntry.end_time, displayEntry.break_minutes))} h`
                                        : isFixedDayHoursEntryType(displayType) && isWeekdayYmd(entry.entry_date)
                                          ? `${formatDisplayHours(selected?.expected_hours_per_day ?? 8)} h`
                                        : displayType === 'public_holiday' && isHolidayCreditDateForSelected(entry.entry_date)
                                          ? `${formatDisplayHours(selected?.expected_hours_per_day ?? 8)} h`
                                          : ''}
                                    </span>
                                    {entryClosed ? (
                                      <Lock className="w-3 h-3 shrink-0 text-app-text-subtle" aria-label="Abrechnungsmonat abgeschlossen" />
                                    ) : null}
                                  </button>
                                  );
                                })}
                            </div>
                          </div>
                        </div>
                      <p className="px-1 text-xs text-app-text-subtle">
                        Feiertage richten sich nach dem in den Einstellungen hinterlegten Bundesland des Betriebs.
                      </p>
                      </div>
                    )}

                    </div>{/* end max-w container */}
                  </div>{/* end overflow-y-auto */}
                </>
              )}
            </div>
          </div>
        </section>
      </main>

      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept=".pdf,image/png,image/jpeg,image/jpg,image/webp"
        onChange={(e) => onSelectUploadFile(e.target.files?.[0] ?? null)}
      />
      <input
        ref={dropFileInputRef}
        type="file"
        className="hidden"
        accept=".pdf,image/png,image/jpeg,image/jpg,image/webp"
        onChange={(e) => onSelectUploadFile(e.target.files?.[0] ?? null)}
      />

      {showWizard && (
        <NewEmployeeWizard
          onClose={() => setShowWizard(false)}
          onSave={createEmployee}
          showInviteHint={canInviteUser}
          canAccessInternal={canAccessInternal}
        />
      )}

      {docUploadModalOpen && (
        <div className="fixed inset-0 z-[110] grid place-items-center">
          <button type="button" className="absolute inset-0 bg-black/25" onClick={() => setDocUploadModalOpen(false)} />
          <div className="relative w-[420px] max-w-[92vw] rounded-xl border border-app-border bg-app-surface p-5 shadow-xl text-center">
            <h4 className="text-sm font-semibold text-app-text">Dokumentkategorie wählen</h4>
            <p className="text-xs text-app-text-subtle mt-1 break-all">{pendingFile?.name}</p>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {uploadCategoryOptions.map((row) => (
                <button
                  key={row.key}
                  type="button"
                  onClick={() => setPendingCategory(row.key)}
                  className={`px-2.5 py-1 rounded-md border text-xs ${
                    pendingCategory === row.key
                      ? 'bg-blue-50 border-blue-300 text-blue-700'
                      : 'bg-app-surface border-app-border text-app-text-muted'
                  }`}
                >
                  {row.label}
                </button>
              ))}
            </div>
            <div className="mt-4 flex justify-center gap-2">
              <button type="button" onClick={() => setDocUploadModalOpen(false)} className="px-3 py-1.5 rounded-md border border-app-border-strong text-sm">
                Abbrechen
              </button>
              <button
                type="button"
                onClick={() => void uploadDocument()}
                disabled={!pendingFile || uploadingDoc}
                className="px-3 py-1.5 rounded-md bg-pipedrive-blue text-white text-sm disabled:opacity-50"
              >
                {uploadingDoc ? 'Lädt…' : 'Hochladen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {docEditModalOpen && (
        <div className="fixed inset-0 z-[112] grid place-items-center">
          <button type="button" className="absolute inset-0 bg-black/25" onClick={() => setDocEditModalOpen(false)} />
          <div className="relative w-[420px] max-w-[92vw] rounded-xl border border-app-border bg-app-surface p-5 shadow-xl">
            <h4 className="text-sm font-semibold text-app-text">Dokument bearbeiten</h4>
            <div className="mt-4 space-y-3">
              <label className="block">
                <span className="text-xs font-medium text-app-text-muted">Dateiname</span>
                <input
                  type="text"
                  value={docEditName}
                  onChange={(e) => setDocEditName(e.target.value)}
                  className="mt-1 w-full rounded-md border border-app-border-strong px-3 py-2 text-sm text-app-text outline-none focus:border-blue-500"
                />
              </label>
              <div>
                <span className="text-xs font-medium text-app-text-muted">Kategorie</span>
                <div className="mt-2 flex flex-wrap gap-2">
                  {editCategoryOptions.map((row) => (
                    <button
                      key={row.key}
                      type="button"
                      onClick={() => setDocEditCategory(row.key)}
                      className={`px-2.5 py-1 rounded-md border text-xs ${
                        docEditCategory === row.key
                          ? 'bg-blue-50 border-blue-300 text-blue-700'
                          : 'bg-app-surface border-app-border text-app-text-muted'
                      }`}
                    >
                      {row.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setDocEditModalOpen(false)} className="px-3 py-1.5 rounded-md border border-app-border-strong text-sm">
                Abbrechen
              </button>
              <button
                type="button"
                onClick={() => void saveDocumentEdit()}
                disabled={!docEditName.trim() || savingDocEdit}
                className="px-3 py-1.5 rounded-md bg-pipedrive-blue text-white text-sm disabled:opacity-50"
              >
                {savingDocEdit ? 'Speichert…' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingDeleteDoc && (
        <div
          className="fixed inset-0 z-[113] flex items-center justify-center p-4 bg-black/40"
          onClick={() => !deletingDocId && setPendingDeleteDoc(null)}
          role="presentation"
        >
          <div
            className="w-full max-w-md rounded-xl border border-app-border bg-app-surface p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-employee-document-title"
          >
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 id="delete-employee-document-title" className="text-base font-semibold text-app-text">
                Dokument löschen
              </h2>
              <button
                type="button"
                onClick={() => setPendingDeleteDoc(null)}
                disabled={Boolean(deletingDocId)}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded text-app-text-subtle transition-colors hover:bg-app-hover hover:text-app-text-muted disabled:opacity-50"
                aria-label="Schließen"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <p className="text-sm text-app-text-muted leading-relaxed">
              Möchtest du <span className="font-semibold">{pendingDeleteDoc.display_name || pendingDeleteDoc.id}</span> wirklich löschen?
            </p>

            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setPendingDeleteDoc(null)}
                disabled={Boolean(deletingDocId)}
                className="btn-pipedrive-secondary"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={() => void confirmDeleteDocument()}
                disabled={Boolean(deletingDocId)}
                className="app-btn-danger-outline inline-flex items-center justify-center rounded-lg border border-red-200 bg-app-surface px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
              >
                {deletingDocId ? 'Löschen...' : 'Löschen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {inviteModalOpen && canInviteUser && selected && (
        <UserInviteModal
          canManage={canManageInvites}
          presets={rolePresets}
          loading={inviting}
          success={false}
          error={inviteModalError}
          title="Nutzer einladen"
          subtitle={[selected.first_name, selected.last_name].filter(Boolean).join(' ') || 'Mitarbeiter'}
          initialChannel="phone"
          initialEmail={selected.email || ''}
          initialPhone={selected.phone || ''}
          initialFirstName={selected.first_name || ''}
          initialLastName={selected.last_name || ''}
          onClose={closeInviteModal}
          onSubmit={submitInviteAccess}
        />
      )}

      <AppConfirmDialog
        open={Boolean(pendingDeleteEmployee)}
        onClose={() => !deletingEmployeeId && setPendingDeleteEmployee(null)}
        title="Mitarbeiter löschen"
        destructive
        confirmLabel="In Papierkorb legen"
        isLoading={Boolean(deletingEmployeeId) || deleteImpactLoading}
        onConfirm={() => void confirmDeleteEmployee()}
        description={
          pendingDeleteEmployee ? (
            <div className="space-y-3">
              <p>
                Möchtest du{' '}
                <span className="font-semibold text-app-text">
                  {[pendingDeleteEmployee.first_name, pendingDeleteEmployee.last_name].filter(Boolean).join(' ')
                    || pendingDeleteEmployee.id}
                </span>{' '}
                löschen?
              </p>
              <p className="text-app-text-muted">
                28 Tage wiederherstellbar, danach endgültig gelöscht (inkl. Dateien).
              </p>
              {deleteImpactLoading ? (
                <InlineLoadingState label="Auswirkungen werden geladen…" className="justify-start" />
              ) : deleteImpact ? (
                <ul className="list-disc pl-5 space-y-1 text-app-text-muted">
                  <li>{deleteImpact.documents} Dokument(e)</li>
                  <li>{deleteImpact.timeEntries} Zeiteinträge</li>
                  <li>{deleteImpact.changeRequests} Änderungsanträge</li>
                  <li>{deleteImpact.payrollEntries} Lohnbuchungen</li>
                  <li>{deleteImpact.payrollRules} Lohnregeln</li>
                  <li>{deleteImpact.activityAssignees + deleteImpact.primaryActivities} Plantafel-Verknüpfungen</li>
                </ul>
              ) : null}
              {deleteImpact?.hasLinkedUser ? (
                <p className="font-medium text-amber-800">
                  Der verknüpfte Nutzerzugang wird sofort gesperrt und mit archiviert.
                </p>
              ) : null}
            </div>
          ) : null
        }
      />

      {entryModalDate && (
        <div className="fixed inset-0 z-[115] grid place-items-center">
          <button type="button" className="absolute inset-0 bg-black/25" onClick={() => setEntryModalDate(null)} />
          <div className="relative w-[420px] max-w-[92vw] rounded-xl border border-app-border bg-app-surface p-5 shadow-xl">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <h4 className="text-sm font-semibold text-app-text">Tageseintrag</h4>
                {entryModalLinkedEntry ? (
                  <p className="mt-0.5 truncate text-xs text-app-text-subtle">
                    {entryModalLinkedEntry.activity_id
                      ? `Termin: ${entryModalLinkedEntry.activity_title || 'ohne Titel'}`
                      : `Projekt: ${entryModalLinkedEntry.lead_title || 'ohne Titel'}`}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => setEntryModalDate(null)}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded text-app-text-subtle transition-colors hover:bg-app-hover hover:text-app-text-muted"
                aria-label="Schließen"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            {entryModalInClosedMonth ? (
              <PayrollPeriodClosedBanner isApprover={isPayrollApprover} className="mt-3" />
            ) : null}
            {entryModalDate && hasPendingChangeRequest(entryModalDate) && !entryModalNotice ? (
              <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                Für diesen Tag liegt eine beantragte Änderung vor.
              </div>
            ) : null}
            {entryModalNotice ? (
              <div className="mt-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900">
                {entryModalNotice}
              </div>
            ) : null}
            {entryModalLinked.length > 0 ? (
              <div className="mt-3 rounded-md border border-app-border bg-app-surface-muted px-3 py-2">
                <p className="text-xs font-medium text-app-text-muted">Weitere Einträge an diesem Tag</p>
                <ul className="mt-1 space-y-0.5">
                  {entryModalLinked.map((entry) => {
                    const hours = calcWorkHours(entry.start_time, entry.end_time, entry.break_minutes);
                    const target = entry.activity_id
                      ? `Termin: ${entry.activity_title || 'ohne Titel'}`
                      : `Projekt: ${entry.lead_title || 'ohne Titel'}`;
                    const label = `${toHm(entry.start_time) || '—'}–${toHm(entry.end_time) || '—'} · ${formatDisplayHours(hours)} h · ${target}`;
                    return (
                      <li key={entry.id} className="text-xs text-app-text">
                        {canEditIntervals ? (
                          <button
                            type="button"
                            onClick={() => openEntryModal(entry.entry_date, { entry })}
                            className="text-left hover:text-pipedrive-blue hover:underline"
                            title="Diesen Eintrag bearbeiten"
                          >
                            {label}
                          </button>
                        ) : (
                          label
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
            <label className="block mt-2">
              <span className="text-xs font-medium text-app-text-muted">Datum</span>
              <DatePicker
                value={entryModalDate}
                onChange={(nextDate) => {
                  if (!nextDate) return;
                  if (entryModalLinkedEntry) setEntryModalDate(nextDate);
                  else openEntryModal(nextDate, { fresh: entryModalFresh });
                }}
                className="mt-1"
              />
            </label>
            <div className="mt-4 space-y-3">
              <label className="block">
                <span className="text-xs font-medium text-app-text-muted">Typ</span>
                <AppSelect
                  value={entryDraft.entry_type}
                  onChange={(value) => {
                    const nextType = value as TimeEntry['entry_type'];
                    const tracksHours = isHourTrackingEntryType(nextType);
                    setEntryDraft((prev) => ({
                      ...prev,
                      entry_type: nextType,
                      break_minutes: tracksHours ? prev.break_minutes : '0',
                      start_time: tracksHours ? prev.start_time : '',
                      end_time: tracksHours ? prev.end_time : '',
                    }));
                  }}
                  options={ENTRY_TYPE_ORDER.map((type) => ({
                    value: type,
                    label: ENTRY_TYPE_LABEL[type],
                  }))}
                  className="mt-1"
                  size="md"
                />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <LabeledInput
                  label="Start"
                  type="time"
                  value={entryDraft.start_time}
                  onChange={(v) => setEntryDraft((prev) => ({ ...prev, start_time: v }))}
                  disabled={!isHourTrackingEntryType(entryDraft.entry_type)}
                />
                <LabeledInput
                  label="Ende"
                  type="time"
                  value={entryDraft.end_time}
                  onChange={(v) => setEntryDraft((prev) => ({ ...prev, end_time: v }))}
                  disabled={!isHourTrackingEntryType(entryDraft.entry_type)}
                />
              </div>
              {isHourTrackingEntryType(entryDraft.entry_type) ? (
                <LabeledInput
                  label="Pause (Minuten)"
                  type="number"
                  min={0}
                  step={1}
                  value={entryDraft.break_minutes}
                  onChange={(v) => setEntryDraft((prev) => ({ ...prev, break_minutes: v }))}
                />
              ) : null}
              <label className="block">
                <span className="text-xs font-medium text-app-text-muted">Notiz</span>
                <textarea
                  value={entryDraft.note}
                  onChange={(e) => setEntryDraft((prev) => ({ ...prev, note: e.target.value }))}
                  className="mt-1 w-full rounded-md border border-app-border-strong px-3 py-2 text-sm min-h-20"
                />
              </label>
            </div>
            {entryOverlapWarning ? (
              <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                <p>{entryOverlapWarning}</p>
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    onClick={() => void saveTimeEntry(true)}
                    disabled={savingTimeEntry}
                    className="rounded-md border border-amber-300 bg-white px-2 py-1 font-medium hover:bg-amber-100 disabled:opacity-50"
                  >
                    Trotzdem speichern
                  </button>
                  <button
                    type="button"
                    onClick={() => setEntryOverlapWarning(null)}
                    className="px-2 py-1 text-amber-900 hover:underline"
                  >
                    Zeiten anpassen
                  </button>
                </div>
              </div>
            ) : null}
            <div className="mt-4 flex items-center justify-between gap-2">
              {canDeleteEntry ? (
                <button
                  type="button"
                  onClick={() => void deleteTimeEntry()}
                  disabled={deletingTimeEntry}
                  className="app-btn-danger-outline inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-red-200 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Löschen
                </button>
              ) : (
                <span />
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setEntryModalDate(null)}
                  disabled={savingTimeEntry || deletingTimeEntry}
                  className="px-3 py-1.5 rounded-md border border-app-border-strong text-sm disabled:opacity-50"
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  onClick={() => void saveTimeEntry()}
                  disabled={savingTimeEntry || deletingTimeEntry}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-pipedrive-blue text-white text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {savingTimeEntry ? <LoadingSpinner size="sm" /> : null}
                  {savingTimeEntry ? 'Speichert…' : 'Speichern'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {error && (
        <div className="fixed bottom-20 right-4 z-[130] rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 shadow">
          {error}
        </div>
      )}
      </div>
      <InboxAttachmentPreviewModal
        attachment={previewDocument}
        onClose={() => setPreviewDocument(null)}
      />
    </>
  );
}

type VacationBalanceMetrics = {
  year: number;
  entitlement: number;
  fullYear: number;
  statutoryFull: number;
  additionalFull: number;
  isProrated: boolean;
  hasPartialStartMonth: boolean;
  remaining: number;
  takenYear: number;
  plannedYear: number;
  takenMonth: number;
  plannedMonth: number;
};

function VacationBalanceInfo({ balance }: { balance: VacationBalanceMetrics }) {
  const contractLine =
    balance.additionalFull > 0
      ? `${formatVacationDayCount(balance.statutoryFull)} gesetzlicher + ${formatVacationDayCount(balance.additionalFull)} Zusatzurlaub = ${formatVacationDayCount(balance.fullYear)} Tage Urlaub/Jahr`
      : balance.fullYear > 0
        ? `${formatVacationDayCount(balance.fullYear)} Tage Urlaub/Jahr`
        : null;

  const prorationLine = balance.isProrated
    ? balance.hasPartialStartMonth
      ? `Anspruch ${balance.year}: ${formatVacationDayCount(balance.entitlement)} Tage (Einstiegsmonat nach Kalendertagen, danach je 1/12 Monatsanteil).`
      : `Anspruch ${balance.year}: ${formatVacationDayCount(balance.entitlement)} Tage (anteilig je 1/12 pro beschäftigtem Monat).`
    : balance.fullYear > 0
      ? `Anspruch ${balance.year}: ${formatVacationDayCount(balance.entitlement)} Tage.`
      : null;

  const lines = [
    contractLine,
    prorationLine,
    `${formatVacationDayCount(balance.takenYear)} genommen`,
    `${formatVacationDayCount(balance.plannedYear)} geplant (vom Rest abgezogen)`,
    `${formatVacationDayCount(balance.takenMonth + balance.plannedMonth)} im angezeigten Monat`,
  ].filter((line): line is string => Boolean(line));

  return (
    <div className="relative shrink-0 group/vacation-info">
      <button
        type="button"
        className="grid h-5 w-5 place-items-center rounded-full text-app-text-subtle hover:bg-app-hover hover:text-app-text-muted"
        aria-label="Details zum Urlaubsanspruch"
      >
        <Info className="w-3.5 h-3.5" />
      </button>
      <div
        role="tooltip"
        className="pointer-events-none absolute left-1/2 bottom-full z-20 mb-1.5 hidden w-max max-w-[260px] -translate-x-1/2 rounded-lg border border-app-border bg-app-surface px-3 py-2 text-xs leading-relaxed text-app-text-muted shadow-lg group-hover/vacation-info:block group-focus-within/vacation-info:block"
      >
        {lines.map((line) => (
          <p key={line}>{line}</p>
        ))}
      </div>
    </div>
  );
}

function MetricCard({
  icon,
  label,
  value,
  infoContent,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  infoContent?: React.ReactNode;
}) {
  return (
    <div className="rounded-xl bg-app-surface border border-app-border shadow-sm px-4 py-3">
      <div className="flex items-center gap-2">
        {icon}
        <p className="text-lg font-bold text-app-text">{value}</p>
        {infoContent}
      </div>
      <p className="text-xs text-app-text-subtle mt-1">{label}</p>
    </div>
  );
}

// German driving license classes in standard order
const STAMMDATEN_FIELD_LABEL_CLASS =
  'text-xs font-medium text-app-text-muted';

/** Matches the h-9 text inputs so selects and text fields line up in every form on this page. */
const FORM_SELECT_TRIGGER_CLASS = '!min-h-0 h-9 !rounded-md !py-0';
const STAMMDATEN_SELECT_TRIGGER_CLASS = `${FORM_SELECT_TRIGGER_CLASS} !text-sm`;

const LICENSE_CLASSES = ['AM', 'A1', 'A2', 'A', 'B1', 'B', 'BE', 'C1', 'C', 'C1E', 'CE', 'D1', 'D', 'D1E', 'DE', 'L', 'T'] as const;

// Group label hints shown as tooltip
const LICENSE_HINTS: Record<string, string> = {
  AM: 'Moped', A1: 'Leichtkraftrad', A2: 'Kraftrad mittel', A: 'Kraftrad',
  B1: 'Leichtkfz', B: 'PKW', BE: 'PKW + Anhänger',
  C1: 'LKW bis 7,5t', C: 'LKW', C1E: 'LKW 7,5t + Anh.', CE: 'LKW + Anh.',
  D1: 'Minibus', D: 'Bus', D1E: 'Minibus + Anh.', DE: 'Bus + Anh.',
  L: 'Land-/Forstw.', T: 'Traktor',
};

function LicenseClassPicker({ value, editMode, onChange }: {
  value: string[];
  editMode: boolean;
  onChange: (v: string[]) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  const selected = new Set(value);

  // Close on outside click
  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggle = (cls: string) => {
    const next = new Set(selected);
    if (next.has(cls)) next.delete(cls); else next.add(cls);
    onChange(LICENSE_CLASSES.filter((c) => next.has(c)));
  };

  // View mode: just show badges
  if (!editMode) {
    if (selected.size === 0) return <span className="text-sm text-app-text-subtle">–</span>;
    return (
      <div className="mt-0.5 flex flex-wrap gap-1">
        {LICENSE_CLASSES.filter((c) => selected.has(c)).map((cls) => (
          <span
            key={cls}
            title={LICENSE_HINTS[cls]}
            className="rounded bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700"
          >
            {cls}
          </span>
        ))}
      </div>
    );
  }

  const label = selected.size === 0
    ? 'Klassen auswählen…'
    : LICENSE_CLASSES.filter((c) => selected.has(c)).join(', ');

  return (
    <div ref={ref} className="relative">
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`flex h-9 w-full items-center justify-between gap-1.5 rounded-md border bg-app-surface px-2.5 text-left text-sm outline-none ${
          open ? 'border-pipedrive-blue' : 'border-app-border'
        } ${selected.size === 0 ? 'text-app-text-subtle' : 'text-app-text'}`}
      >
        <span className="min-w-0 flex-1 truncate">{label}</span>
        <ChevronDown
          className={`h-3 w-3 shrink-0 text-app-text-subtle transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>

      {/* Dropdown popover */}
      {open && (
        <div className="absolute left-0 top-[calc(100%+4px)] z-[200] min-w-[280px] rounded-lg border border-app-border bg-app-surface p-3 shadow-lg">
          <div className="grid grid-cols-4 gap-1.5">
            {LICENSE_CLASSES.map((cls) => {
              const on = selected.has(cls);
              return (
                <button
                  key={cls}
                  type="button"
                  title={LICENSE_HINTS[cls]}
                  onClick={() => toggle(cls)}
                  className={`flex flex-col items-center justify-center rounded-md border-[1.5px] px-1 py-1.5 transition-colors ${
                    on
                      ? 'border-pipedrive-blue bg-pipedrive-blue/15'
                      : 'border-app-border bg-app-surface-muted hover:bg-app-hover'
                  }`}
                >
                  <span className={`text-xs font-bold ${on ? 'text-pipedrive-blue' : 'text-app-text'}`}>{cls}</span>
                  <span
                    className={`mt-px text-center text-xs leading-tight ${
                      on ? 'text-pipedrive-blue' : 'text-app-text-subtle'
                    }`}
                  >
                    {LICENSE_HINTS[cls]}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="mt-2.5 flex items-center justify-between border-t border-app-border pt-2">
            <span className="text-xs text-app-text-subtle">{selected.size} ausgewählt</span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="px-1.5 py-0.5 text-xs font-semibold text-pipedrive-blue"
            >
              Fertig
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function CompensationTypeSwitch({
  value,
  editMode,
  onChange,
}: {
  value: EmployeeCompensationType;
  editMode: boolean;
  onChange: (value: EmployeeCompensationType) => void;
}) {
  if (!editMode) {
    return (
      <span className="ml-auto text-xs font-semibold text-app-text">
        {COMPENSATION_TYPE_OPTIONS.find((o) => o.value === value)?.label ?? value}
      </span>
    );
  }
  return (
    <div className="ml-auto inline-flex gap-0.5 rounded-md border border-app-border bg-app-surface-muted p-0.5">
      {COMPENSATION_TYPE_OPTIONS.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value as EmployeeCompensationType)}
            className={`rounded px-2.5 py-1 text-xs font-semibold transition-colors ${
              active
                ? 'bg-app-surface text-app-text shadow-sm'
                : 'text-app-text-muted hover:text-app-text'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function StammdatenSection({
  title,
  icon,
  fields,
  draft,
  editMode,
  showValidationErrors = false,
  headerExtra,
  onPatch,
  canAccessInternal = true,
}: {
  title: string;
  icon: React.ReactNode;
  fields: Array<{
    label: string;
    key: keyof Employee;
    type?: 'date' | 'number';
    kind?: 'array' | 'license' | 'tax_class' | 'religion' | 'marital_status' | 'employment_type' | 'work_area' | 'compensation_type' | 'boolean';
  }>;
  draft: Employee;
  editMode: boolean;
  showValidationErrors?: boolean;
  headerExtra?: React.ReactNode;
  onPatch: (key: keyof Employee, value: any) => void;
  canAccessInternal?: boolean;
}) {
  const zeroMeansEmptyNumberFields = new Set<keyof Employee>([
    'weekly_hours',
    'statutory_vacation_days',
    'additional_vacation_days',
    'hourly_wage',
    'monthly_salary',
    'allowance_per_hour',
    'vma_tax_free_per_day',
    'overnight_tax_free_per_day',
    'child_allowance_count',
  ] as (keyof Employee)[]);
  const selectOptionsByKind: Record<'tax_class' | 'religion' | 'marital_status' | 'employment_type' | 'work_area', Array<{ value: string; label: string }>> = {
    tax_class: TAX_CLASS_OPTIONS,
    religion: RELIGION_OPTIONS,
    marital_status: MARITAL_STATUS_OPTIONS,
    employment_type: EMPLOYMENT_TYPE_OPTIONS,
    work_area: workAreaSelectOptions(canAccessInternal),
  };

  return (
    <div className="rounded-lg border border-app-border bg-app-surface px-5 py-4 shadow-[0_1px_3px_rgba(0,0,0,0.07)]">
      {/* Section header */}
      <div className="mb-4 flex items-center gap-2 border-b border-app-border pb-3">
        {icon}
        <h3 className="text-sm font-semibold text-app-text">{title}</h3>
        {headerExtra}
      </div>
      {/* Fields grid */}
      <div className="grid grid-cols-3 gap-x-6 gap-y-3.5">
        {fields.map((field) => {
          const rawValue = (draft as any)[field.key];

          if (field.kind === 'license') {
            return (
              <div key={String(field.key)} className="flex flex-col gap-1">
                <span className={STAMMDATEN_FIELD_LABEL_CLASS}>{field.label}</span>
                <LicenseClassPicker
                  value={Array.isArray(rawValue) ? rawValue : []}
                  editMode={editMode}
                  onChange={(v) => onPatch(field.key, v)}
                />
              </div>
            );
          }

          const displayValue =
            field.kind === 'array'
              ? Array.isArray(rawValue) ? rawValue.join(', ') : ''
              : rawValue ?? '';

          const isIbanField = field.key === 'iban';
          const isTaxIdField = field.key === 'tax_id';
          const isBooleanField = field.kind === 'boolean';
          const selectOptions = field.kind && field.kind in selectOptionsByKind ? selectOptionsByKind[field.kind as 'tax_class' | 'religion' | 'marital_status' | 'employment_type' | 'work_area'] : null;
          const ibanInvalid = isIbanField && !isValidIban(displayValue);
          const taxIdInvalid = isTaxIdField && normalizeTaxId(displayValue).length > 0 && normalizeTaxId(displayValue).length !== 11;
          const showIbanError = showValidationErrors && ibanInvalid;
          const showTaxIdError = showValidationErrors && taxIdInvalid;

          return (
            <div key={String(field.key)} className="flex flex-col gap-1">
              <span className={STAMMDATEN_FIELD_LABEL_CLASS}>{field.label}</span>
              {editMode ? (
                isBooleanField ? (
                  <AppSelect
                    value={String(Boolean(rawValue))}
                    onChange={(value) => onPatch(field.key, value === 'true')}
                    options={YES_NO_OPTIONS}
                    size="md"
                    triggerClassName={STAMMDATEN_SELECT_TRIGGER_CLASS}
                  />
                ) : selectOptions ? (
                  <AppSelect
                    value={displayValue}
                    onChange={(value) => onPatch(field.key, value)}
                    options={selectOptions}
                    size="md"
                    triggerClassName={STAMMDATEN_SELECT_TRIGGER_CLASS}
                  />
                ) : (
                <input
                  type={field.type === 'date' ? 'date' : 'text'}
                  value={field.type === 'date' ? (displayValue || '') : displayValue}
                  inputMode={field.type === 'number' ? 'decimal' : isTaxIdField ? 'numeric' : undefined}
                  maxLength={isTaxIdField ? 11 : undefined}
                  onChange={(e) => {
                    if (field.kind === 'array') {
                      onPatch(field.key, e.target.value.split(',').map((x) => x.trim()).filter(Boolean));
                    } else if (field.type === 'number') {
                      onPatch(field.key, e.target.value);
                    } else if (isIbanField) {
                      onPatch(field.key, normalizeIban(e.target.value));
                    } else if (field.key === 'nationality_code') {
                      onPatch(field.key, e.target.value.toUpperCase().slice(0, 2));
                    } else if (isTaxIdField) {
                      onPatch(field.key, normalizeTaxId(e.target.value));
                    } else {
                      onPatch(field.key, e.target.value);
                    }
                  }}
                  className={`h-9 rounded-md border bg-app-surface px-2.5 text-sm text-app-text outline-none focus:border-pipedrive-blue focus:ring-2 focus:ring-pipedrive-blue/20 ${
                    showIbanError || showTaxIdError ? 'border-red-400' : 'border-app-border'
                  } ${isIbanField ? 'font-mono' : ''}`}
                />
                )
              ) : (
                <span
                  className={`text-sm ${displayValue ? 'text-app-text' : 'text-app-text-subtle'} ${
                    isIbanField ? 'font-mono' : ''
                  }`}
                >
                  {field.type === 'date'
                    ? formatDate(displayValue || null)
                    : isIbanField
                      ? (formatIban(displayValue) || '-')
                      : field.type === 'number'
                        ? (() => {
                            const parsed = parseLocaleNumber(displayValue, Number.NaN);
                            if (!Number.isFinite(parsed)) return '-';
                            if (parsed === 0 && zeroMeansEmptyNumberFields.has(field.key)) return '-';
                            return formatLocaleNumber(displayValue);
                          })()
                      : isBooleanField
                        ? (Boolean(rawValue) ? 'Ja' : 'Nein')
                        : selectOptions
                          ? (selectOptions.find((opt) => opt.value === displayValue)?.label || '-')
                          : displayValue || '-'}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
